import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
// Cache bust: 2025-09-14-07:49
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, signIn, signOut, signUp, confirmSignUp, resendSignUpCode, resetPassword, confirmResetPassword, updatePassword, updateUserAttribute } from '@aws-amplify/auth';
import { apiUrl, awsConfig, getApiEndpoint } from './src/config/aws-config';
import MobileDashboard from './MobileDashboard';
import { AgreementStatusProvider, useAgreementStatus } from './src/agreements/AgreementStatusContext.jsx';
import TermsAgreementView from './src/agreements/TermsAgreementView.jsx';
import SmsAgreementView from './src/agreements/SmsAgreementView.jsx';
import DashboardAgreementsSection from './src/agreements/DashboardAgreementsSection.jsx';
import DisclosuresSection from './src/documents/DisclosuresSection.jsx';
import HelpSupportView from './src/components/HelpSupportView.jsx';
import ComponentStatus from './src/components/ComponentStatus.jsx';
import { AutoApproveReconfirmBlock, AutoApproveReconfirmModal } from './src/components/AutoApproveReconfirm.jsx';
import { computeAutoApproveReconfirmUi } from './src/utils/autoApproveReconfirmUi.js';
import { formatPhoneToE164 } from './src/utils/phoneFormat.js';
import { normalizeUsername, validateResetConfirmFields } from './src/auth/passwordReset.js';
import { parseRegisterDeepLinkFromWindow } from './src/auth/registerDeepLink.js';
import {
  RISK_PARAM_LABELS,
  DEFAULT_MAX_DAILY_TRADES,
  DEFAULT_MAX_SINGLE_TRADE_AMOUNT,
  DEFAULT_PRICE_TOLERANCE_THRESHOLD,
  ELIGIBLE_ORDER_TYPE_OPTIONS,
  createDefaultMappingFormState,
  applyChannelSelectionToMappingForm,
  applySyncedDollarLimitsToForm,
  normalizeEligibleOrderTypes,
  syncMaxDollarLimitFields,
  resolveMaxDailyDollarExposure,
  CHALLENGE_TIER_DOLLAR_LIMIT_HELP,
  DEFAULT_TRADE_SIZE_MULTIPLIER,
  ALL_TRADE_SIZE_MULTIPLIER_OPTIONS,
  parseTradeSizeMultiplier,
  tradeSizeMultiplierForSave,
  tradeSizeMultiplierFromMapping,
  formatDeniedTickersForDisplay,
  deniedTickersForSave,
  deniedTickersSummaryForMapping,
  hasDeniedTickers,
  MAX_DENIED_TICKERS,
} from './src/utils/riskParameters.js';
import { isMaskedCredential, isBulletPlaceholder, shouldSendCredentialOnSave } from './src/utils/credentialMasking.js';
import { revealStoredCredential } from './src/utils/credentialReveal.js';
import {
  createInitialFormState,
  createInitialUserSettings,
  createInitialAccountSettings,
  createInitialValidationState,
  createInitialMappingValidationState,
  createInitialHistoryFilters,
  createInitialAdminUserSummary,
  createInitialChannelEditForm,
  isSessionSnapshotCleared,
} from './src/utils/userSessionState.js';

const isDev = import.meta.env.DEV;

const isE2eTradeHistoryChannelFilterTest = () =>
  isDev &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('e2e_trade_history_channel_filter');

/** Cognito may return groups as array or string; pool may use Admin, Admins, admin, etc. */
const normalizeCognitoGroups = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) return [raw.trim()];
  return [];
};

const userHasAdminGroup = (groups) => {
  const g = normalizeCognitoGroups(groups);
  return g.some((name) => ['Admins', 'admin', 'Admin', 'SuperAdmins'].includes(name));
};
const devLog = (...args) => { if (isDev) console.log(...args); };

/** Session key for ?skip-whop=<code> → Cognito clientMetadata.skip_whop (must match backend WHOP_SKIP_WHOP_SECRET). */
const WHOP_SKIP_SESSION_KEY = 'whop_skip_whop';

const parseWhopSkipFromUrl = () => {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const skip = params.get('skip-whop');
    if (skip) {
      sessionStorage.setItem(WHOP_SKIP_SESSION_KEY, skip);
      const url = new URL(window.location.href);
      url.searchParams.delete('skip-whop');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }
  } catch (e) {
    if (isDev) console.warn('parseWhopSkipFromUrl:', e);
  }
};

const getWhopSkipClientMetadata = () => {
  try {
    const v = sessionStorage.getItem(WHOP_SKIP_SESSION_KEY);
    return v ? { skip_whop: v } : undefined;
  } catch {
    return undefined;
  }
};

// Cooldown before user can request another verification email (Cognito may throttle; 60s is a safe default)
const RESEND_VERIFICATION_COOLDOWN_SEC = 60;
const PASSWORD_RESET_RESEND_COOLDOWN_SEC = 60;

const analyticsPeriodLabel = (period) => {
  const labels = { '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 90 days', '1y': 'Last year' };
  return labels[period] || 'Last 30 days';
};

// Configure AWS Amplify from single config source (aws-config.js, updated by deploy scripts)
if (isDev && awsConfig.Auth?.Cognito) {
  console.log('[Auth debug] Cognito pool in use:', awsConfig.Auth.Cognito.userPoolId, '| client:', awsConfig.Auth.Cognito.userPoolClientId);
}
Amplify.configure({ Auth: awsConfig.Auth });

// Helper to attach Cognito ID token to API requests
const buildAuthHeaders = async () => {
  try {
    const session = await fetchAuthSession();
    const idToken = session?.tokens?.idToken?.toString();
    devLog('Auth session:', session ? 'exists' : 'null');
    devLog('ID token:', idToken ? 'exists' : 'null');
    return idToken ? { Authorization: `Bearer ${idToken}` } : {};
  } catch (e) {
    if (isDev) console.error('Failed to build auth headers:', e);
    return {};
  }
};

// Generic copy when Cognito reports a disabled user (no public membership-status API; #47).
const WHOP_ACCESS_DISABLED_MESSAGE =
  'Whop membership is required to access this app. Please ensure you have an active Whop membership to proceed.';

// Check if sign-in error indicates a disabled user (Cognito returns "User is not enabled")
const isDisabledUserError = (error) => {
  const msg = (error?.message || '').toLowerCase();
  const name = (error?.name || '').toLowerCase();
  return msg.includes('not enabled') || msg.includes('disabled') || msg.includes('user is disabled') || name.includes('userdisabled');
};

/** Desktop nav tab: active vs enabled-but-inactive vs disabled (no access). */
const navTabClassName = (isActive, canAccess) => {
  const base = 'px-3 py-2.5 rounded-md text-sm font-medium min-h-[44px]';
  if (isActive) return `${base} bg-blue-100 text-blue-700`;
  if (canAccess) return `${base} text-gray-500 hover:text-gray-700`;
  return `${base} text-gray-300 cursor-not-allowed`;
};

  // Helper function to safely extract user attributes from tokens
  const extractUserAttributes = (tokens) => {
    if (!tokens || !tokens.idToken) {
      devLog('No tokens or ID token available');
      return null;
    }

    devLog('Token structure:', {
      hasPayload: !!tokens.idToken.payload,
      hasClaims: !!tokens.idToken.claims,
      tokenKeys: Object.keys(tokens.idToken)
    });

    let email, phone_number, created_at;

    if (tokens.idToken.payload) {
      email = tokens.idToken.payload.email;
      phone_number = tokens.idToken.payload.phone_number;
      created_at = tokens.idToken.payload['custom:created_at'] || new Date().toISOString();
    } else if (tokens.idToken.claims) {
      email = tokens.idToken.claims.email;
      phone_number = tokens.idToken.claims.phone_number;
      created_at = tokens.idToken.claims['custom:created_at'] || new Date().toISOString();
    } else {
      devLog('Unexpected token structure:', tokens.idToken);
      return null;
    }

    return { email, phone_number, created_at };
  };

const AppInner = () => {
  const [appInitialized, setAppInitialized] = useState(false);

  useEffect(() => {
    parseWhopSkipFromUrl();
  }, []);
  const [user, setUser] = useState(null);
  const [currentView, setCurrentView] = useState('dashboard');
  const [formState, setFormState] = useState(createInitialFormState);
  const [userSettings, setUserSettings] = useState(createInitialUserSettings);
  const [accountSettings, setAccountSettings] = useState(createInitialAccountSettings);
  const [userData, setUserData] = useState(null);
  const [tokenStatusChecked, setTokenStatusChecked] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [hasTradierToken, setHasTradierToken] = useState(false);
  const [hasMappingApiToken, setHasMappingApiToken] = useState(false);
  const [revealBusy, setRevealBusy] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [validationState, setValidationState] = useState(createInitialValidationState);
  const [validating, setValidating] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [isLoginView, setIsLoginView] = useState(() => !parseRegisterDeepLinkFromWindow());
  /** null = normal login/register; send_code = email step; set_password = code + new password */
  const [forgotPasswordView, setForgotPasswordView] = useState(null);
  const [passwordResetCode, setPasswordResetCode] = useState('');
  const [passwordResetResendCooldownRemaining, setPasswordResetResendCooldownRemaining] = useState(0);
  const [passwordResetResendLoading, setPasswordResetResendLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [resendCooldownRemaining, setResendCooldownRemaining] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [tempUser, setTempUser] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Trading history state
  const [tradeHistory, setTradeHistory] = useState([]);
  const [tradeHistoryError, setTradeHistoryError] = useState('');
  const [tradingAnalytics, setTradingAnalytics] = useState({});
  const [historyFilters, setHistoryFilters] = useState(createInitialHistoryFilters);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('30d');
  const [currentPage, setCurrentPage] = useState(1);

  // Multi-channel configuration state
  const [availableChannels, setAvailableChannels] = useState([]);
  const [availablePlatforms, setAvailablePlatforms] = useState([]);
  const [channelMappings, setChannelMappings] = useState([]);
  const [channelMappingsChecked, setChannelMappingsChecked] = useState(false);
  const [userMultiplierEnabled, setUserMultiplierEnabled] = useState(false);
  const [autoApproveReconfirmModalMapping, setAutoApproveReconfirmModalMapping] = useState(null);
  const [autoApproveReconfirmBusy, setAutoApproveReconfirmBusy] = useState(false);
  const [autoApproveReconfirmFromApi, setAutoApproveReconfirmFromApi] = useState(null);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [showMappingForm, setShowMappingForm] = useState(false);
  const [mappingFormState, setMappingFormState] = useState(createDefaultMappingFormState);
  const [consentSigningUrls, setConsentSigningUrls] = useState({ terms: '', sms: '' });
  const [startingConsentType, setStartingConsentType] = useState('');
  const [consentActionError, setConsentActionError] = useState('');
  const [consentsDetail, setConsentsDetail] = useState([]);
  const [downloadingTermsPackage, setDownloadingTermsPackage] = useState(false);
  const [downloadingSmsConsentPdf, setDownloadingSmsConsentPdf] = useState(false);
  const [showMappingToken, setShowMappingToken] = useState(false);
  const [mappingValidationState, setMappingValidationState] = useState(createInitialMappingValidationState);
  const [mappingValidated, setMappingValidated] = useState(false);
  const [validatingMapping, setValidatingMapping] = useState(false);

  // Edit channel (the Discord channel itself) state
  const [selectedChannelForEdit, setSelectedChannelForEdit] = useState(null);
  const [channelEditForm, setChannelEditForm] = useState(createInitialChannelEditForm);
  const [loadingChannelDetails, setLoadingChannelDetails] = useState(false);
  const [savingChannelEdit, setSavingChannelEdit] = useState(false);

  // Manual channel entry (type channel ID instead of dropdown)
  const [manualChannelEntry, setManualChannelEntry] = useState(false);

  // Admin state
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUserSummary, setAdminUserSummary] = useState(createInitialAdminUserSummary);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserTrades, setSelectedUserTrades] = useState([]);
  const [selectedUserTradesTotal, setSelectedUserTradesTotal] = useState(0);
  const [userLifecycleLoading, setUserLifecycleLoading] = useState(false);
  const [userLifecycleError, setUserLifecycleError] = useState('');
  const [deleteConfirmUserId, setDeleteConfirmUserId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);
  // Admin CloudWatch log viewer (parity with monitoring/tail_logs.py)
  const [adminLogGroupOptions, setAdminLogGroupOptions] = useState([]);
  const [adminLogGroupsSelected, setAdminLogGroupsSelected] = useState([]);
  const [adminLogsMinutesBack, setAdminLogsMinutesBack] = useState(5);
  const [adminLogsKeyword, setAdminLogsKeyword] = useState('');
  const [adminLogsIncludeError, setAdminLogsIncludeError] = useState(false);
  const [adminLogsIncludeDebug, setAdminLogsIncludeDebug] = useState(false);
  const [adminLogsEvents, setAdminLogsEvents] = useState([]);
  const [adminLogsLoading, setAdminLogsLoading] = useState(false);
  const [adminLogsError, setAdminLogsError] = useState('');
  const [adminLogsFilterPattern, setAdminLogsFilterPattern] = useState(null);
  const [adminLogsAutoRefresh, setAdminLogsAutoRefresh] = useState(false);
  const [adminSubView, setAdminSubView] = useState('users');
  const [adminLogGroupDropdownOpen, setAdminLogGroupDropdownOpen] = useState(false);
  const [adminLogGroupSearch, setAdminLogGroupSearch] = useState('');
  const ADMIN_DEFAULT_CORE_LOG_GROUPS = [
    '/ecs/tradingbot-order-monitor',
    '/ecs/tradingbot-discordbot',
    '/aws/lambda/tradingbot-deviation-retry-attempt',
    '/aws/lambda/tradingbot-discord-channel-user-discovery',
    '/aws/lambda/tradingbot-trade-processor',
    '/aws/lambda/tradingbot-order-monitor',
  ];
  const adminLogGroupMenuRef = useRef(null);
  const ADMIN_USERS_PAGE_SIZE = 10;
  const ADMIN_TRADE_HISTORY_PAGE_SIZE = 10;
  const [adminUserSearchQuery, setAdminUserSearchQuery] = useState('');
  const [adminUserPage, setAdminUserPage] = useState(1);
  const [adminUserSearchError, setAdminUserSearchError] = useState('');
  const [adminTradeHistoryPage, setAdminTradeHistoryPage] = useState(1);
  const [selectedUserMappings, setSelectedUserMappings] = useState([]);
  const [loadingUserMappings, setLoadingUserMappings] = useState(false);
  const [selectedUserAuditLog, setSelectedUserAuditLog] = useState([]);
  const [loadingUserAuditLog, setLoadingUserAuditLog] = useState(false);
  const [isMobileDashboard, setIsMobileDashboard] = useState(false);

  const adminUserTotalPages = Math.max(
    1,
    Math.ceil(adminUsers.length / ADMIN_USERS_PAGE_SIZE)
  );

  const pagedAdminUsers = useMemo(() => {
    const start = (adminUserPage - 1) * ADMIN_USERS_PAGE_SIZE;
    return adminUsers.slice(start, start + ADMIN_USERS_PAGE_SIZE);
  }, [adminUsers, adminUserPage]);

  useEffect(() => {
    setAdminUserPage(1);
  }, [adminUserSearchQuery]);

  useEffect(() => {
    if (adminUserPage > adminUserTotalPages) {
      setAdminUserPage(adminUserTotalPages);
    }
  }, [adminUserPage, adminUserTotalPages]);

  const adminTradeHistoryTotalPages = Math.max(
    1,
    Math.ceil((selectedUserTradesTotal || 0) / ADMIN_TRADE_HISTORY_PAGE_SIZE)
  );

  const {
    hasAcceptedTerms,
    hasAcceptedSms,
    setHasAcceptedTerms,
    setHasAcceptedSms,
  } = useAgreementStatus();

  const activeUserEmailRef = useRef(null);

  const resetAllUserState = useCallback(() => {
    setFormState(createInitialFormState());
    setUserSettings(createInitialUserSettings());
    setAccountSettings(createInitialAccountSettings());
    setUserData(null);
    setTokenStatusChecked(false);
    setShowToken(false);
    setValidationState(createInitialValidationState());
    setValidating(false);
    setIsValidated(false);
    setTradeHistory([]);
    setTradeHistoryError('');
    setTradingAnalytics({});
    setHistoryFilters(createInitialHistoryFilters());
    setAnalyticsPeriod('30d');
    setCurrentPage(1);
    setAvailableChannels([]);
    setAvailablePlatforms([]);
    setChannelMappings([]);
    setChannelMappingsChecked(false);
    setUserMultiplierEnabled(false);
    setAutoApproveReconfirmModalMapping(null);
    setAutoApproveReconfirmBusy(false);
    setAutoApproveReconfirmFromApi(null);
    setSelectedMapping(null);
    setShowMappingForm(false);
    setMappingFormState(createDefaultMappingFormState());
    setConsentSigningUrls({ terms: '', sms: '' });
    setConsentsDetail([]);
    setShowMappingToken(false);
    setMappingValidationState(createInitialMappingValidationState());
    setMappingValidated(false);
    setValidatingMapping(false);
    setSelectedChannelForEdit(null);
    setChannelEditForm(createInitialChannelEditForm());
    setManualChannelEntry(false);
    setAdminUsers([]);
    setAdminUserSummary(createInitialAdminUserSummary());
    setSelectedUser(null);
    setSelectedUserTrades([]);
    setSelectedUserTradesTotal(0);
    setAdminLogGroupOptions([]);
    setAdminLogGroupsSelected([]);
    setAdminLogsMinutesBack(5);
    setAdminLogsKeyword('');
    setAdminLogsIncludeError(false);
    setAdminLogsIncludeDebug(false);
    setAdminLogsEvents([]);
    setAdminLogsLoading(false);
    setAdminLogsError('');
    setAdminLogsFilterPattern(null);
    setAdminLogsAutoRefresh(false);
    setAdminSubView('users');
    setAdminLogGroupDropdownOpen(false);
    setAdminLogGroupSearch('');
    setAdminUserSearchQuery('');
    setAdminUserPage(1);
    setAdminUserSearchError('');
    setAdminTradeHistoryPage(1);
    setSelectedUserMappings([]);
    setLoadingUserMappings(false);
    setHasAcceptedTerms(false);
    setHasAcceptedSms(false);
    setError('');
    setMessage('');
  }, [setHasAcceptedTerms, setHasAcceptedSms]);

  const markActiveUserEmail = useCallback((email) => {
    if (!email) {
      activeUserEmailRef.current = null;
      return;
    }
    if (activeUserEmailRef.current && activeUserEmailRef.current !== email) {
      resetAllUserState();
    }
    activeUserEmailRef.current = email;
  }, [resetAllUserState]);

  // Derived access flags for gated views
  const hasTradingConfig = channelMappings.length > 0;
  // SMS consent is optional — only Terms acceptance gates channel access.
  const hasCompletedAgreements = hasAcceptedTerms;
  const canAccessChannelConfig = hasCompletedAgreements && Boolean(user && !isAdmin);
  const canAccessTradeHistory = hasCompletedAgreements && hasTradingConfig;
  /** Documents tab: available as soon as the user is logged in (signed downloads gated separately). */
  const canAccessDocumentsTab = Boolean(user && !isAdmin);
  /** Signed consent package downloads (API-gated): after Terms acceptance and trading config. */
  const canDownloadSignedConsentPackages = hasCompletedAgreements && hasTradingConfig;

  const termsConsentRow = useMemo(
    () =>
      Array.isArray(consentsDetail)
        ? consentsDetail.find((c) => c.consentType === 'terms')
        : null,
    [consentsDetail]
  );
  const smsConsentRow = useMemo(
    () =>
      Array.isArray(consentsDetail)
        ? consentsDetail.find((c) => c.consentType === 'sms')
        : null,
    [consentsDetail]
  );
  const termsReminderMessage = termsConsentRow?.needsReconfirmation
    ? 'Periodic reconfirmation is required for your terms agreement.'
    : '';
  const smsReminderMessage = smsConsentRow?.needsReconfirmation
    ? 'Periodic reconfirmation is required for your SMS consent.'
    : '';

  const isLoggedIn = user !== null;

  const sessionSnapshotForTests = useMemo(() => ({
    formState,
    userSettings,
    accountSettings,
    userData,
    tradeHistory,
    tradeHistoryError,
    tradingAnalytics,
    channelMappings,
    availableChannels,
    availablePlatforms,
    selectedUserTrades,
    selectedUserMappings,
  }), [
    formState,
    userSettings,
    accountSettings,
    userData,
    tradeHistory,
    tradeHistoryError,
    tradingAnalytics,
    channelMappings,
    availableChannels,
    availablePlatforms,
    selectedUserTrades,
    selectedUserMappings,
  ]);

  useEffect(() => {
    if (!isDev || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has('e2e_reset_test')) return;

    window.__e2eResetTest = {
      seedStaleUserSession: () => {
        setUser(null);
        setIsAdmin(false);
        setTradeHistory([{ symbol: 'STALE_SYMBOL', action: 'BUY', status: 'filled' }]);
        setChannelMappings([{ channel_id: 'stale-channel', channel_name: 'stale-channel' }]);
        setFormState((prev) => ({
          ...prev,
          tradierAccountId: 'STALE123',
          tradierToken: 'stale-token',
        }));
        setSelectedUserTrades([{ symbol: 'ADMIN_STALE' }]);
      },
      resetAllUserState,
      getSessionSnapshot: () => sessionSnapshotForTests,
      isSessionCleared: () => isSessionSnapshotCleared(sessionSnapshotForTests),
    };
  }, [resetAllUserState, sessionSnapshotForTests]);

  useEffect(() => {
    if (!isDev || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has('e2e_trade_history_channel_filter')) return;

    window.__e2eTradeHistoryTest = {
      seedTradeHistoryChannelFilterView: () => {
        setUser({
          attributes: { email: 'e2e-trade-history@test.local' },
        });
        setIsAdmin(false);
        setAdminChecked(true);
        setHasAcceptedTerms(true);
        setHasAcceptedSms(true);
        setChannelMappings([
          { id: 'map-e2e-1', channel_id: '111111', channel_name: 'My Subscribed Channel' },
        ]);
        setAvailableChannels([
          { channel_id: '111111', channel_name: 'My Subscribed Channel' },
          { channel_id: '999999', channel_name: 'Global Challenge Not Subscribed' },
        ]);
        setTradeHistory([]);
        setCurrentView('trading_history');
      },
      getChannelFilterOptionLabels: () =>
        Array.from(
          document.querySelector('[data-testid="trade-history-channel-filter"]')?.options || [],
        ).map((option) => option.textContent?.trim() || ''),
    };
  }, [setHasAcceptedTerms, setHasAcceptedSms]);

  useEffect(() => {
    checkAuthState();
  }, []);

  useEffect(() => {
    if (currentView === 'agreements_terms' && hasAcceptedTerms) {
      setConsentSigningUrls((prev) => ({ ...prev, terms: '' }));
      setCurrentView('dashboard');
    }
    if (currentView === 'agreements_sms' && hasAcceptedSms) {
      setConsentSigningUrls((prev) => ({ ...prev, sms: '' }));
      setCurrentView('dashboard');
    }
    if (currentView === 'agreements_sms' && !hasAcceptedTerms) {
      setConsentSigningUrls((prev) => ({ ...prev, sms: '' }));
      setCurrentView('dashboard');
    }
  }, [currentView, hasAcceptedTerms, hasAcceptedSms]);

  useEffect(() => {
    const detectMobileDashboard = () => {
      setIsMobileDashboard(window.innerWidth <= 768);
    };

    detectMobileDashboard();
    window.addEventListener('resize', detectMobileDashboard);
    return () => window.removeEventListener('resize', detectMobileDashboard);
  }, []);

  // Load trading data when view changes
  // Note: trading_analytics view is temporarily disabled in the UI
  useEffect(() => {
    if (user && currentView === 'dashboard') {
      loadTradingAnalytics({ silent: true });
    }
    if (user && currentView === 'trading_history' && !isAdmin) {
      if (!isE2eTradeHistoryChannelFilterTest()) {
        loadTradeHistory();
        loadChannelMappings(); // User's subscribed channels for filter dropdown
      }
    }
    if (user && currentView === 'channel_config' && !isAdmin) {
      loadChannelMappings();
      loadAvailableChannels();
      loadAvailablePlatforms();
    }
  }, [currentView, user, historyFilters, currentPage, analyticsPeriod, isAdmin]);

  useEffect(() => {
    if (!user) {
      return;
    }
    // Admins use navbar (Manage users / System health / Live tail logs), not Trade history / Channel config
    if (
      isAdmin &&
      (currentView === 'trading_history' || currentView === 'channel_config')
    ) {
      setCurrentView('dashboard');
      return;
    }
    if (currentView === 'trading_history' && !canAccessTradeHistory) {
      setCurrentView('dashboard');
    }
    if (currentView === 'channel_config' && !canAccessChannelConfig) {
      setCurrentView('dashboard');
    }
    if (currentView === 'documents' && !canAccessDocumentsTab) {
      setCurrentView('dashboard');
    }
  }, [user, currentView, canAccessTradeHistory, canAccessChannelConfig, canAccessDocumentsTab, isAdmin]);

  // Clear error and message when navigating between views
  // Use a ref to track the previous view to avoid clearing on initial mount
  const prevViewRef = useRef(currentView);
  useEffect(() => {
    // Only clear messages if we're actually changing views (not on initial mount)
    if (prevViewRef.current !== currentView && prevViewRef.current !== null) {
      // Clear messages when navigating away from a view
      // This prevents messages from showing on pages where they weren't set
      setError('');
      setMessage('');
    }
    prevViewRef.current = currentView;
  }, [currentView]);

  // Resend verification code cooldown
  useEffect(() => {
    if (!isVerifying || resendCooldownRemaining <= 0) return;
    const t = setInterval(() => {
      setResendCooldownRemaining((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [isVerifying, resendCooldownRemaining]);

  // Password reset: resend code cooldown (same pattern as email verification)
  useEffect(() => {
    if (forgotPasswordView !== 'set_password' || passwordResetResendCooldownRemaining <= 0) return; // pragma: allowlist secret
    const t = setInterval(() => {
      setPasswordResetResendCooldownRemaining((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [forgotPasswordView, passwordResetResendCooldownRemaining]);

  // Prefill configuration form when opening configuration view
  useEffect(() => {
    const loadUserConfiguration = async () => {
      try {
        setLoadingConfig(true);
        devLog('Loading user configuration...');
        const authHeaders = await buildAuthHeaders();
        devLog('Auth headers:', authHeaders);

        // Use fetch directly to avoid CORS preflight
        const response = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/configuration`, {
          method: 'GET',
          headers: {
            'Authorization': authHeaders.Authorization || ''
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const cfg = data?.configuration ?? data;

        if (cfg) {
          devLog('📥 Configuration loaded:', cfg);
          devLog('📥 tradierUseSandbox from backend:', cfg.tradierUseSandbox, 'type:', typeof cfg.tradierUseSandbox);
          devLog('📥 tradier_use_sandbox from backend:', cfg.tradier_use_sandbox, 'type:', typeof cfg.tradier_use_sandbox);
          setHasTradierToken(Boolean(cfg.hasTradierToken ?? cfg.tradierToken));
          setFormState(prev => ({
            ...prev,
            tradierToken: cfg.tradierToken || '',
            tradierAccountId: cfg.tradierAccountId || cfg.tradier_account_id || prev.tradierAccountId,
            discordUserId: cfg.discordUserId || cfg.discord_user_id || prev.discordUserId,
            tradierUseSandbox: cfg.tradierUseSandbox !== undefined ? Boolean(cfg.tradierUseSandbox) : (cfg.tradier_use_sandbox !== undefined ? Boolean(cfg.tradier_use_sandbox) : false),
            // Load risk management settings
            priceDeviationLimit: cfg.priceDeviationLimit !== undefined ? Number(cfg.priceDeviationLimit) : prev.priceDeviationLimit,
            enablePriceChecks: cfg.enablePriceChecks !== undefined ? Boolean(cfg.enablePriceChecks) : prev.enablePriceChecks,
            maxDailyTrades: cfg.maxDailyTrades !== undefined ? Number(cfg.maxDailyTrades) : prev.maxDailyTrades,
            maxSingleTradeAmount: cfg.maxSingleTradeAmount !== undefined ? Number(cfg.maxSingleTradeAmount) : prev.maxSingleTradeAmount,
            maxDailyDollarExposure: cfg.maxDailyDollarExposure !== undefined
              ? Number(cfg.maxDailyDollarExposure)
              : (cfg.maxSingleTradeAmount !== undefined
                ? Number(cfg.maxSingleTradeAmount)
                : prev.maxDailyDollarExposure),
            autoApproveEnabled: cfg.autoApproveEnabled !== undefined ? Boolean(cfg.autoApproveEnabled) : prev.autoApproveEnabled,
            positionMultiplier: cfg.positionMultiplier !== undefined ? Number(cfg.positionMultiplier) : prev.positionMultiplier,
          }));
          // Reset show token state when loading new config
          setShowToken(false);
          // Reset validation state when loading existing config
          setValidationState({
            discordUserId: { status: 'pending', message: '' },
            tradierAccountId: { status: 'pending', message: '' },
            tradierToken: { status: 'pending', message: '' }
          });
        }
      } catch (e) {
        // Non-fatal if config not found
        // console.debug('No existing configuration to prefill', e);
        // Reset validation state when no config is found
        setValidationState({
          discordUserId: { status: 'pending', message: '' },
          tradierAccountId: { status: 'pending', message: '' },
          tradierToken: { status: 'pending', message: '' }
        });
      } finally {
        setLoadingConfig(false);
      }
    };

    if (user && currentView === 'configuration') {
      // Reset validation state when navigating to configuration page
      setValidationState({
        discordUserId: { status: 'pending', message: '' },
        tradierAccountId: { status: 'pending', message: '' },
        tradierToken: { status: 'pending', message: '' }
      });
      loadUserConfiguration();
    }
  }, [user, currentView]);

  const checkAuthState = async () => {
    try {
      devLog('Checking auth state...');
      const authSession = await fetchAuthSession();
      devLog('Full auth session:', authSession);

      const { tokens } = authSession;
      devLog('Auth session tokens:', tokens);

      if (!tokens) {
        devLog('No tokens in auth session');
        activeUserEmailRef.current = null;
        resetAllUserState();
        setUser(null);
        setUserData(null);
        setIsAdmin(false);
        setAdminChecked(false);
        return;
      }

      const attributes = extractUserAttributes(tokens);
      if (!attributes) {
        devLog('No authenticated user or no valid attributes');
        activeUserEmailRef.current = null;
        resetAllUserState();
        setUser(null);
        setUserData(null);
        setIsAdmin(false);
        setAdminChecked(false);
        return;
      }

      markActiveUserEmail(attributes.email);

      const currentUser = {
        attributes: {
          email: attributes.email,
          phone_number: attributes.phone_number,
          created_at: attributes.created_at
        }
      };

      setUser(currentUser);
      await loadUserData(currentUser);
      await loadUserSettings(currentUser);

      // Check if user is admin via Cognito groups (only once per session)
      if (!adminChecked) {
      const adminAuthSession = await fetchAuthSession();
      const groups = adminAuthSession?.tokens?.idToken?.payload?.['cognito:groups'] || [];
      if (userHasAdminGroup(groups)) {
        setIsAdmin(true);
        setAdminUsers([]);
        }
        setAdminChecked(true);
      }
    } catch (error) {
      devLog('No authenticated user:', error);
      activeUserEmailRef.current = null;
      resetAllUserState();
      setUser(null);
      setUserData(null);
      setIsAdmin(false);
      setAdminChecked(false);
    } finally {
      setAppInitialized(true);
    }
  };

  const loadUserData = async (currentUser) => {
    try {
      if (!currentUser) {
        devLog('No user data to load');
        return;
      }

      // In a real app, you might fetch user profile from your database
      // For now, we'll use Cognito attributes
      const attributes = currentUser.attributes || {};
      setUserData({
        email: attributes.email,
        phoneNumber: attributes.phone_number,
        hasTokens: false, // Will be updated when tokens are added
        createdAt: attributes.created_at || new Date().toISOString(),
      });

      // Check if user has tokens by calling the API (only once per session)
      if (!tokenStatusChecked) {
      try {
        devLog('Calling /users/tokens/status endpoint...');

        // Check current session before making API call
        const currentSession = await fetchAuthSession();
        devLog('Current session before API call:', currentSession);

          const authHeaders = await buildAuthHeaders();
          devLog('Token status auth headers:', authHeaders);

          const response = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/tokens/status`, {
            method: 'GET',
            headers: {
              'Authorization': authHeaders.Authorization || ''
            }
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          devLog('Token status data:', data);
          if (data && (data.hasTokens === true || data.has_tokens === true)) {
          setUserData(prev => ({ ...prev, hasTokens: true }));
        }
          setTokenStatusChecked(true);
      } catch (tokenError) {
        devLog('Error checking token status:', tokenError);
        devLog('Full error object:', JSON.stringify(tokenError, null, 2));
        // Don't fail the entire user data load for this
        devLog('This is expected if the endpoint is not yet implemented');
          setTokenStatusChecked(true);
        }
      }

    // Load channel mappings once per session for account status/setup required
    if (!channelMappingsChecked) {
      try {
        await loadChannelMappings();
      } finally {
        setChannelMappingsChecked(true);
      }
    }

    // Load consent status from backend (SignWell-backed terms, SMS consent)
    try {
      const authHeaders = await buildAuthHeaders();
      const agreementRes = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/consents/status`, {
        method: 'GET',
        headers: { Authorization: authHeaders.Authorization || '' },
      });
      if (agreementRes.ok) {
        const agreementData = await agreementRes.json();
        setHasAcceptedTerms(!!agreementData.hasAcceptedTerms);
        setHasAcceptedSms(!!agreementData.hasAcceptedSms);
        setConsentsDetail(Array.isArray(agreementData.consents) ? agreementData.consents : []);
      }
    } catch (agreementErr) {
      devLog('Error loading consent status:', agreementErr);
    }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const refreshConsentStatus = async (options = {}) => {
    const suppressConsentError = !!options.suppressConsentError;
    try {
      const authHeaders = await buildAuthHeaders();
      const res = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/consents/status`, {
        method: 'GET',
        headers: { Authorization: authHeaders.Authorization || '' },
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();
      setHasAcceptedTerms(!!data.hasAcceptedTerms);
      setHasAcceptedSms(!!data.hasAcceptedSms);
      if (Array.isArray(data.consents)) {
        setConsentsDetail(data.consents);
      }
      return data;
    } catch (err) {
      console.error('Failed to refresh consent status:', err);
      if (!suppressConsentError) {
        setConsentActionError('Unable to refresh signing status. Please try again.');
      }
      return null;
    }
  };

  /** Poll after SignWell closes — webhook may update the DB a few seconds later. */
  const refreshConsentStatusAfterSigning = async (consentType) => {
    const flag = consentType === 'terms' ? 'hasAcceptedTerms' : 'hasAcceptedSms';
    const waitMs = [0, 2000, 3000, 5000, 8000];
    let last = null;
    setConsentActionError('');
    for (const delay of waitMs) {
      if (delay > 0) {
        await new Promise((r) => {
          setTimeout(r, delay);
        });
      }
      last = await refreshConsentStatus({ suppressConsentError: true });
      if (last && last[flag]) {
        return last;
      }
    }
    return last;
  };

  const startConsentSigning = async (consentType) => {
    setConsentActionError('');
    // Clear any stale iframe URL before starting a fresh request.
    setConsentSigningUrls((prev) => ({ ...prev, [consentType]: '' }));
    setStartingConsentType(consentType);
    try {
      const authHeaders = await buildAuthHeaders();
      const res = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/consents/${consentType}/start`, {
        method: 'POST',
        headers: {
          Authorization: authHeaders.Authorization || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload.error || `Failed to start ${consentType} signing`);
      }
      if (!payload.signingUrl) {
        throw new Error('No signing URL returned by backend');
      }
      setConsentSigningUrls((prev) => ({ ...prev, [consentType]: payload.signingUrl }));
    } catch (err) {
      console.error(`Error starting ${consentType} signing:`, err);
      setConsentSigningUrls((prev) => ({ ...prev, [consentType]: '' }));
      setConsentActionError(err.message || 'Failed to start secure signing.');
    } finally {
      setStartingConsentType('');
    }
  };

  const downloadTermsPackage = async () => {
    if (!canDownloadSignedConsentPackages || downloadingTermsPackage) {
      return;
    }
    setConsentActionError('');
    setDownloadingTermsPackage(true);
    try {
      const authHeaders = await buildAuthHeaders();
      const res = await fetch(
        `${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/consents/terms/download`,
        {
          method: 'GET',
          headers: { Authorization: authHeaders.Authorization || '' },
        }
      );
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to generate terms package');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'phoenix-standing-instruction-package.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download terms package:', err);
      setConsentActionError(err.message || 'Failed to download terms package.');
    } finally {
      setDownloadingTermsPackage(false);
    }
  };

  const downloadSmsConsentPdf = async () => {
    if (!canDownloadSignedConsentPackages || downloadingSmsConsentPdf) {
      return;
    }
    setConsentActionError('');
    setDownloadingSmsConsentPdf(true);
    try {
      const authHeaders = await buildAuthHeaders();
      const res = await fetch(
        `${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/consents/sms/download`,
        {
          method: 'GET',
          headers: { Authorization: authHeaders.Authorization || '' },
        }
      );
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to download SMS consent PDF');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sms-consent-signed.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download SMS consent PDF:', err);
      setConsentActionError(err.message || 'Failed to download SMS consent PDF.');
    } finally {
      setDownloadingSmsConsentPdf(false);
    }
  };

  const loadUserSettings = async (currentUser) => {
    try {
      setLoadingDashboard(true);
      devLog('Loading user settings...');

      // Call the backend API to get user settings
      const authHeaders = await buildAuthHeaders();
      const response = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/settings`, {
        method: 'GET',
        headers: {
          'Authorization': authHeaders.Authorization || ''
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      devLog('Settings loaded data:', data);

      // Update user settings with the response
      if (data && data.settings) {
        devLog('Settings received:', data.settings);
        setUserSettings(prev => ({
          ...prev,
          priceDeviationLimit: data.settings.priceDeviationLimit ?? prev.priceDeviationLimit ?? 10.0,
          maxDailyTrades: data.settings.maxDailyTrades ?? prev.maxDailyTrades ?? DEFAULT_MAX_DAILY_TRADES,
          autoApproveEnabled: data.settings.autoApproveEnabled ?? prev.autoApproveEnabled ?? false,
          enablePriceChecks: data.settings.enablePriceChecks ?? prev.enablePriceChecks ?? true,
          maxSingleTradeAmount: data.settings.maxSingleTradeAmount ?? prev.maxSingleTradeAmount ?? 5000.0,
          positionMultiplier: data.settings.positionMultiplier ?? prev.positionMultiplier ?? 1,
          ...data.settings
        }));
      } else {
        console.warn('No settings found in response:', data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // Don't fail the entire user data load for this
      devLog('Using default settings if API call fails');
    } finally {
      setLoadingDashboard(false);
    }
  };

  const loadTradeHistory = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        ...historyFilters,
        offset: (currentPage - 1) * historyFilters.limit
      });

      const authHeaders = await buildAuthHeaders();
      const response = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/trading/history?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': authHeaders.Authorization || ''
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setTradeHistory(data.trades || []);
      setTradeHistoryError(data.error || '');
    } catch (error) {
      console.error('Error loading trade history:', error);
      setError('Failed to load trade history');
      setTradeHistoryError('');
    } finally {
      setLoading(false);
    }
  };

  const loadTradingAnalytics = async (opts = {}) => {
    const silent = opts.silent === true;
    try {
      if (!silent) {
        setLoading(true);
      } else {
        setLoadingAnalytics(true);
      }
      const authHeaders = await buildAuthHeaders();
      const response = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/trading/analytics?period=${analyticsPeriod}`, {
        method: 'GET',
        headers: {
          'Authorization': authHeaders.Authorization || ''
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setTradingAnalytics(data);
    } catch (error) {
      console.error('Error loading trading analytics:', error);
      setTradingAnalytics({
        error: error?.message || 'Failed to load trading analytics',
      });
      if (!silent) {
        setError('Failed to load trading analytics');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      } else {
        setLoadingAnalytics(false);
      }
    }
  };

  // Multi-channel configuration functions
  const loadAvailableChannels = async () => {
    try {
      const authHeaders = await buildAuthHeaders();
      const response = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/available-channels`, {
        method: 'GET',
        headers: {
          'Authorization': authHeaders.Authorization || ''
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAvailableChannels(data.channels || []);
    } catch (error) {
      console.error('Error loading available channels:', error);
    }
  };

  const loadChannelDetails = async (channelId) => {
    if (!channelId) return;
    setLoadingChannelDetails(true);
    try {
      const authHeaders = await buildAuthHeaders();
      const response = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/channels/${channelId}`, {
        method: 'GET',
        headers: { 'Authorization': authHeaders.Authorization || '' }
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const ch = data.channel || {};
      setChannelEditForm({
        channel_name: ch.channel_name || '',
        guild_name: ch.guild_name || ''
      });
    } catch (error) {
      console.error('Error loading channel details:', error);
      setMessage('');
      setError(error.message || 'Failed to load channel details');
    } finally {
      setLoadingChannelDetails(false);
    }
  };

  const saveChannelEdit = async (channelId) => {
    if (!channelId) return;
    setSavingChannelEdit(true);
    setError('');
    setMessage('');
    try {
      const authHeaders = await buildAuthHeaders();
      const response = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/channels/${channelId}`, {
        method: 'PUT',
        headers: {
          'Authorization': authHeaders.Authorization || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          channel_name: channelEditForm.channel_name,
          guild_name: channelEditForm.guild_name
        })
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP error! status: ${response.status}`);
      }
      setMessage('Channel updated successfully.');
      setSelectedChannelForEdit(null);
      setChannelEditForm({ channel_name: '', guild_name: '' });
      loadAvailableChannels();
    } catch (error) {
      console.error('Error saving channel:', error);
      setError(error.message || 'Failed to save channel');
    } finally {
      setSavingChannelEdit(false);
    }
  };

  const loadAvailablePlatforms = async () => {
    try {
      const authHeaders = await buildAuthHeaders();
      const response = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/trading-platforms`, {
        method: 'GET',
        headers: {
          'Authorization': authHeaders.Authorization || ''
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAvailablePlatforms(data.platforms || []);
    } catch (error) {
      console.error('Error loading available platforms:', error);
    }
  };

  const promptRevealPassword = () => {
    const password = window.prompt('Enter your account password to view the stored API token:');
    return password ? String(password) : null;
  };

  const handleRevealConfigurationToken = async () => {
    if (revealBusy) return;
    const password = promptRevealPassword();
    if (!password) return;
    setRevealBusy(true);
    setError('');
    try {
      const authHeaders = await buildAuthHeaders();
      const api = import.meta.env.VITE_API_URL || getApiEndpoint();
      const data = await revealStoredCredential(api, authHeaders, {
        password,
        scope: 'configuration',
      });
      setFormState((prev) => ({ ...prev, tradierToken: data.tradierToken || '' }));
      setHasTradierToken(true);
      setShowToken(true);
    } catch (err) {
      setError(err.message || 'Could not reveal token');
    } finally {
      setRevealBusy(false);
    }
  };

  const handleRevealMappingToken = async () => {
    if (revealBusy || !selectedMapping?.id) return;
    const password = promptRevealPassword();
    if (!password) return;
    setRevealBusy(true);
    setError('');
    try {
      const authHeaders = await buildAuthHeaders();
      const api = import.meta.env.VITE_API_URL || getApiEndpoint();
      const data = await revealStoredCredential(api, authHeaders, {
        password,
        scope: 'channel-mapping',
        mappingId: selectedMapping.id,
      });
      setMappingFormState((prev) => ({ ...prev, api_token: data.apiToken || '' }));
      setHasMappingApiToken(true);
      setShowMappingToken(true);
    } catch (err) {
      setError(err.message || 'Could not reveal token');
    } finally {
      setRevealBusy(false);
    }
  };

  const loadChannelMappings = async () => {
    try {
      const authHeaders = await buildAuthHeaders();
      const response = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/channel-account-mappings`, {
        method: 'GET',
        headers: {
          'Authorization': authHeaders.Authorization || ''
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setChannelMappings(data.mappings || []);
      setUserMultiplierEnabled(Boolean(data.multiplierEnabled ?? data.multiplier_enabled));
      setAutoApproveReconfirmFromApi(data.autoApproveReconfirm ?? null);
      if (data.hasSmsPhoneOnFile !== undefined) {
        setUserSettings(prev => ({
          ...prev,
          hasSmsPhoneOnFile: data.hasSmsPhoneOnFile,
        }));
      }
    } catch (error) {
      console.error('Error loading channel mappings:', error);
    }
  };

  const submitAutoApproveReconfirm = async () => {
    const m = autoApproveReconfirmModalMapping;
    if (!m?.id) return;
    setAutoApproveReconfirmBusy(true);
    setError('');
    try {
      const authHeaders = await buildAuthHeaders();
      const base = import.meta.env.VITE_API_URL || getApiEndpoint();
      const response = await fetch(
        `${base}/users/channel-account-mappings/${m.id}/auto-approve/reconfirm`,
        {
          method: 'POST',
          headers: {
            Authorization: authHeaders.Authorization || '',
            'Content-Type': 'application/json',
          },
          body: '{}',
        }
      );
      const errBody = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(errBody.error || `HTTP error! status: ${response.status}`);
      }
      setAutoApproveReconfirmModalMapping(null);
      setMessage('Auto-approve reconfirmed. Your quarterly cycle has been reset.');
      await loadChannelMappings();
    } catch (e) {
      console.error('Auto-approve reconfirm error:', e);
      setError(e.message || 'Failed to reconfirm auto-approve');
    } finally {
      setAutoApproveReconfirmBusy(false);
    }
  };

  const hasSmsPhoneOnFile = useMemo(() => {
    if (userSettings.hasSmsPhoneOnFile === true) return true;
    if (userSettings.hasSmsPhoneOnFile === false) return false;
    return Boolean(formatPhoneToE164(userSettings.smsPhoneNumber || ''));
  }, [userSettings.hasSmsPhoneOnFile, userSettings.smsPhoneNumber]);

  const saveChannelMapping = async (mappingData) => {
    if (!mappingValidated) {
      setError('Please validate your credentials first by clicking "Validate".');
      return;
    }
    const turningAutoOn =
      Boolean(mappingData.auto_approve_enabled) &&
      !(selectedMapping && selectedMapping.auto_approve_enabled);
    if (turningAutoOn && !hasSmsPhoneOnFile) {
      setError(
        'A mobile phone number is required to enable auto-approve (for email and SMS reminders). ' +
          'Open User Settings, add your number under Risk Management → Phone number for SMS alerts, save, then try again.'
      );
      return;
    }
    const isUpdate = selectedMapping && selectedMapping.id;
    if (
      hasAcceptedTerms &&
      isUpdate &&
      !window.confirm(
        'You are changing risk settings. This requires reconfirming the standing instruction consent. Continue?'
      )
    ) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      let deniedTickersPayload;
      try {
        deniedTickersPayload = deniedTickersForSave(mappingData.denied_tickers);
      } catch (deniedErr) {
        setError(deniedErr.message || 'Invalid denied ticker list');
        setLoading(false);
        return;
      }
      const authHeaders = await buildAuthHeaders();

      const url = isUpdate
        ? `${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/channel-account-mappings/${selectedMapping.id}`
        : `${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/channel-account-mappings`;

      // API expects camelCase
      const body = {
        channelId: mappingData.channel_id,
        tradingPlatform: mappingData.trading_platform,
        accountId: mappingData.account_id,
        discordUserId: mappingData.discord_user_id,
        useSandbox: mappingData.use_sandbox,
        tradeSizeMultiplier: tradeSizeMultiplierForSave(
          userMultiplierEnabled,
          mappingData.trade_size_multiplier
        ),
        maxDailyTrades: mappingData.max_daily_trades,
        maxSingleTradeAmount: syncMaxDollarLimitFields(mappingData.max_single_trade_amount),
        priceDeviationLimit: mappingData.price_deviation_limit ?? DEFAULT_PRICE_TOLERANCE_THRESHOLD,
        enablePriceChecks: mappingData.enable_price_checks ?? true,
        deniedTickers: deniedTickersPayload,
        enableDeviationOverride: mappingData.enable_deviation_override ?? false,
        autoApproveEnabled: mappingData.auto_approve_enabled,
        eligibleOrderTypes: normalizeEligibleOrderTypes(mappingData.eligible_order_types),
        platformSettings: {
          eligible_order_types: normalizeEligibleOrderTypes(mappingData.eligible_order_types),
          maximum_daily_dollar_exposure: syncMaxDollarLimitFields(
            mappingData.max_daily_dollar_exposure ?? mappingData.max_single_trade_amount
          ),
        },
      };
      if (shouldSendCredentialOnSave(mappingData.api_token)) {
        body.apiToken = mappingData.api_token;
      }

      const response = await fetch(url, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: {
          'Authorization': authHeaders.Authorization || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let msg = errorData.error || `HTTP error! status: ${response.status}`;
        if (errorData.detail) {
          msg = `${msg} ${errorData.detail}`;
        }
        throw new Error(msg);
      }

      const deniedSaved = formatDeniedTickersForDisplay(deniedTickersPayload);
      const deniedSavedNote = deniedSaved
        ? ` Denied tickers (BTO skip list): ${deniedSaved}.`
        : ' Denied tickers: none (BTO not blocked by symbol).';
      setMessage(
        isUpdate
          ? `Channel mapping updated successfully!${deniedSavedNote}`
          : `Channel mapping created successfully!${deniedSavedNote}`
      );
      setSelectedMapping(null);
      setShowMappingForm(false);
      setMappingFormState(createDefaultMappingFormState());
      setMappingValidationState({
        apiToken: { status: 'pending', message: '' },
        accountId: { status: 'pending', message: '' },
        discordUserId: { status: 'pending', message: '' }
      });
      setMappingValidated(false);
      await loadChannelMappings();
      await refreshConsentStatus({ suppressConsentError: true });
    } catch (error) {
      console.error('Error saving channel mapping:', error);
      setError(error.message || 'Failed to save channel mapping');
    } finally {
      setLoading(false);
    }
  };

  const deleteChannelMapping = async (mappingId) => {
    if (!confirm('Are you sure you want to delete this channel mapping?')) {
      return;
    }

    try {
      setLoading(true);
      const authHeaders = await buildAuthHeaders();

      const response = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/channel-account-mappings/${mappingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': authHeaders.Authorization || ''
        }
      });

      if (!response.ok) {
        let msg = `Request failed (${response.status})`;
        try {
          const errData = await response.json();
          if (errData.error) {
            msg = errData.error;
            if (errData.detail) {
              msg = `${msg} ${errData.detail}`;
            }
          }
        } catch (_) {
          /* ignore parse errors */
        }
        throw new Error(msg);
      }

      setMessage('Channel mapping deleted successfully!');
      await loadChannelMappings();
    } catch (error) {
      console.error('Error deleting channel mapping:', error);
      setError(error?.message || 'Failed to delete channel mapping');
    } finally {
      setLoading(false);
    }
  };

  // Admin functions
  const loadAdminUsers = async (searchQuery) => {
    const q = String(searchQuery || '').trim();
    try {
      setLoading(true);
      setAdminUserSearchError('');
      const authHeaders = await buildAuthHeaders();
      const qs = new URLSearchParams();
      if (q) {
        qs.set('q', q);
      }
      const endpoint = qs.toString() ? `admin/users?${qs}` : 'admin/users';
      const response = await fetch(apiUrl(endpoint), {
        method: 'GET',
        headers: {
          'Authorization': authHeaders.Authorization || ''
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const rows = data.users || [];
      setAdminUsers(
        rows.map((u) => ({
          id: u.id,
          email: u.email,
          cognitoUserId: u.cognito_user_id ?? u.cognitoUserId,
          discordUserId: u.discord_user_id ?? u.discordUserId,
          phoneNumber: u.phone_number ?? u.phoneNumber,
          status: u.status ?? (u.is_active === false ? 'inactive' : 'active'),
          hasChannelMappings: !!(u.has_channel_mappings ?? u.hasChannelMappings),
          multiplierEnabled: !!(u.multiplier_enabled ?? u.multiplierEnabled),
          createdAt: u.created_at ?? u.createdAt,
          updatedAt: u.updated_at ?? u.updatedAt,
          lastLogin: u.last_login ?? u.lastLogin,
        }))
      );
    } catch (error) {
      console.error('Error loading admin users:', error);
      setAdminUsers([]);
      setAdminUserPage(1);
      setError(
        error?.message?.includes('403')
          ? 'Admin API returned 403. Use Cognito group Admin (or admin / Admins / SuperAdmins) or ensure admin email is in Lambda ADMIN_EMAILS.'
          : error?.message || 'Failed to load admin users'
      );
    } finally {
      setLoading(false);
    }
  };

  const loadAdminUserSummary = async () => {
    try {
      const authHeaders = await buildAuthHeaders();
      const response = await fetch(apiUrl('admin/users/summary'), {
        method: 'GET',
        headers: {
          Authorization: authHeaders.Authorization || '',
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setAdminUserSummary({
        totalUsers: Number(data.totalUsers || 0),
        activeUsers: Number(data.activeUsers || 0),
        configuredUsers: Number(data.configuredUsers || 0),
      });
    } catch (error) {
      console.error('Error loading admin user summary:', error);
      setAdminUserSummary({
        totalUsers: 0,
        activeUsers: 0,
        configuredUsers: 0,
      });
    }
  };

  const loadUserTrades = async (userId, page = 1) => {
    try {
      setLoading(true);
      const authHeaders = await buildAuthHeaders();
      const limit = ADMIN_TRADE_HISTORY_PAGE_SIZE;
      const offset = (page - 1) * limit;
      const qs = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });
      const response = await fetch(apiUrl(`admin/users/${userId}/trades?${qs}`), {
        method: 'GET',
        headers: {
          'Authorization': authHeaders.Authorization || ''
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const trades = data.trades || [];
      setSelectedUserTradesTotal(typeof data.total === 'number' ? data.total : trades.length);
      setAdminTradeHistoryPage(page);
      setSelectedUserTrades(
        trades.map((t) => ({
          id: t.id,
          symbol: t.symbol,
          action: t.action,
          quantity: t.quantity,
          price: t.price ?? t.limit_price,
          totalAmount: t.total_amount ?? t.totalAmount,
          status: t.status,
          createdAt: t.created_at ?? t.createdAt,
          filledAt: t.filled_at ?? t.filledAt,
          channelName: t.channel_name ?? t.channelName,
          channelId: t.channel_id ?? t.channelId,
          tradingPlatform: t.trading_platform ?? t.tradingPlatform,
          apiEnvironment: t.api_environment ?? t.apiEnvironment,
          tradierUseSandbox: t.tradier_use_sandbox ?? t.tradierUseSandbox,
        }))
      );
    } catch (error) {
      console.error('Error loading user trades:', error);
      setSelectedUserTrades([]);
      setSelectedUserTradesTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const loadUserChannelMappings = async (userId) => {
    setLoadingUserMappings(true);
    setSelectedUserMappings([]);
    try {
      const authHeaders = await buildAuthHeaders();
      const response = await fetch(
        apiUrl(`admin/users/${userId}/channel-mappings`),
        {
          method: 'GET',
          headers: {
            Authorization: authHeaders.Authorization || '',
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setSelectedUserMappings(data.mappings || []);
    } catch (error) {
      console.error('Error loading channel mappings:', error);
      setSelectedUserMappings([]);
    } finally {
      setLoadingUserMappings(false);
    }
  };

  const loadUserAuditLog = async (userId) => {
    setLoadingUserAuditLog(true);
    setSelectedUserAuditLog([]);
    try {
      const authHeaders = await buildAuthHeaders();
      const response = await fetch(
        apiUrl(`admin/users/${userId}/audit-log`),
        {
          method: 'GET',
          headers: {
            Authorization: authHeaders.Authorization || '',
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setSelectedUserAuditLog(data.auditLog || []);
    } catch (error) {
      console.error('Error loading audit log:', error);
      setSelectedUserAuditLog([]);
    } finally {
      setLoadingUserAuditLog(false);
    }
  };

  const handleUserSelect = async (user) => {
    setAdminTradeHistoryPage(1);
    setSelectedUser(user);
    setUserLifecycleError('');
    setDeleteConfirmUserId(null);
    await Promise.all([
      loadUserTrades(user.id, 1),
      loadUserChannelMappings(user.id),
      loadUserAuditLog(user.id),
    ]);
  };

  const handleUserStatusChange = async (userId, newStatus) => {
    setUserLifecycleLoading(true);
    setUserLifecycleError('');
    try {
      const authHeaders = await buildAuthHeaders();
      const res = await fetch(apiUrl(`admin/users/${userId}/status`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authHeaders.Authorization || '',
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setSelectedUser((u) => ({ ...u, status: newStatus }));
      setAdminUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, status: newStatus } : u))
      );
    } catch (err) {
      setUserLifecycleError(err.message || 'Status update failed');
    } finally {
      setUserLifecycleLoading(false);
    }
  };

  const handleMultiplierToggle = async (userId, enabled) => {
    setUserLifecycleLoading(true);
    setUserLifecycleError('');
    try {
      const authHeaders = await buildAuthHeaders();
      const res = await fetch(apiUrl(`admin/users/${userId}/config`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authHeaders.Authorization || '',
        },
        body: JSON.stringify({ multiplier_enabled: enabled }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setSelectedUser((u) => ({
        ...u,
        multiplierEnabled: data.multiplier_enabled,
      }));
      setAdminUsers((prev) =>
        prev.map((u) =>
          u.id === userId
            ? {
                ...u,
                multiplierEnabled: data.multiplier_enabled,
              }
            : u
        )
      );
    } catch (err) {
      setUserLifecycleError(err.message || 'Multiplier update failed');
    } finally {
      setUserLifecycleLoading(false);
    }
  };

  const handleUserDelete = async (userId) => {
    setUserLifecycleLoading(true);
    setUserLifecycleError('');
    try {
      const authHeaders = await buildAuthHeaders();
      const res = await fetch(apiUrl(`admin/users/${userId}`), {
        method: 'DELETE',
        headers: { Authorization: authHeaders.Authorization || '' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setSelectedUser(null);
      setAdminUsers((prev) => prev.filter((u) => u.id !== userId));
      setDeleteConfirmUserId(null);
    } catch (err) {
      setUserLifecycleError(err.message || 'Delete failed');
    } finally {
      setUserLifecycleLoading(false);
    }
  };

  const fetchAdminRecentLogs = useCallback(async () => {
    const allGroupsSelected =
      adminLogGroupOptions.length > 0 &&
      adminLogGroupsSelected.length === adminLogGroupOptions.length;
    if (!allGroupsSelected && adminLogGroupsSelected.length === 0) {
      setAdminLogsError('Select at least one log group.');
      return;
    }
    setAdminLogsLoading(true);
    setAdminLogsError('');
    try {
      const authHeaders = await buildAuthHeaders();
      const payload = {
        minutesBack: adminLogsMinutesBack,
        keyword: adminLogsKeyword.trim() || undefined,
        includeError: adminLogsIncludeError,
        includeDebug: adminLogsIncludeDebug,
      };
      if (!allGroupsSelected) {
        payload.logGroups = adminLogGroupsSelected;
      }
      const res = await fetch(apiUrl('admin/logs/recent'), {
        method: 'POST',
        headers: {
          Authorization: authHeaders.Authorization || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setAdminLogsEvents(data.events || []);
      setAdminLogsFilterPattern(data.filterPattern ?? null);
    } catch (e) {
      console.error('admin logs/recent:', e);
      setAdminLogsError(e.message || 'Failed to load logs');
      setAdminLogsEvents([]);
    } finally {
      setAdminLogsLoading(false);
    }
  }, [
    adminLogGroupOptions,
    adminLogGroupsSelected,
    adminLogsMinutesBack,
    adminLogsKeyword,
    adminLogsIncludeError,
    adminLogsIncludeDebug,
  ]);

  useEffect(() => {
    if (currentView !== 'admin' || !user || !isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const authHeaders = await buildAuthHeaders();
        const res = await fetch(apiUrl('admin/log-groups'), {
          headers: { Authorization: authHeaders.Authorization || '' },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const groups = data.logGroups || [];
        if (cancelled) return;
        setAdminLogGroupOptions(groups);
        setAdminLogGroupsSelected((prev) => {
          if (prev.length === 0) {
            const coreDefaults = ADMIN_DEFAULT_CORE_LOG_GROUPS.filter((g) => groups.includes(g));
            return coreDefaults.length > 0 ? coreDefaults : [...groups];
          }
          return prev.filter((g) => groups.includes(g));
        });
      } catch (err) {
        if (!cancelled) console.error('admin log-groups:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentView, user, isAdmin]);

  useEffect(() => {
    if (currentView !== 'admin' || !adminLogsAutoRefresh) return;
    const id = setInterval(() => {
      fetchAdminRecentLogs();
    }, 15000);
    return () => clearInterval(id);
  }, [currentView, adminLogsAutoRefresh, fetchAdminRecentLogs]);

  useEffect(() => {
    if (!adminLogGroupDropdownOpen) return;
    const onDocMouseDown = (e) => {
      if (adminLogGroupMenuRef.current && !adminLogGroupMenuRef.current.contains(e.target)) {
        setAdminLogGroupDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [adminLogGroupDropdownOpen]);

  useEffect(() => {
    if (currentView !== 'admin' || adminSubView !== 'users' || !isAdmin) return;
    const q = adminUserSearchQuery.trim();
    setAdminUserSearchError('');
    const id = setTimeout(() => {
      loadAdminUsers(q);
    }, 250);
    return () => clearTimeout(id);
  }, [adminUserSearchQuery, currentView, adminSubView, isAdmin]);

  useEffect(() => {
    if (currentView !== 'dashboard' || !isAdmin || !user) return;
    loadAdminUserSummary();
  }, [currentView, isAdmin, user]);

  const toggleAdminLogGroup = (name) => {
    setAdminLogGroupsSelected((prev) =>
      prev.includes(name) ? prev.filter((g) => g !== name) : [...prev, name]
    );
  };

  const allLogGroupsSelected =
    adminLogGroupOptions.length > 0 &&
    adminLogGroupsSelected.length === adminLogGroupOptions.length;

  const toggleAllAdminLogGroups = () => {
    if (allLogGroupsSelected) {
      setAdminLogGroupsSelected([]);
    } else {
      setAdminLogGroupsSelected([...adminLogGroupOptions]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Reset validation if credential fields are changed
    if (name === 'tradierToken' || name === 'tradierAccountId' || name === 'discordUserId') {
      setIsValidated(false);
      setMessage('');
      // Reset validation state for the changed field
      setValidationState(prev => ({
        ...prev,
        [name]: { status: 'pending', message: '' }
      }));
    }

    if (name.startsWith('setting_')) {
      const settingName = name.replace('setting_', '');
      setUserSettings(prev => ({
        ...prev,
        [settingName]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) : value)
      }));
    } else if (name.startsWith('account_')) {
      const accountName = name.replace('account_', '');
      let newValue = value;
      if (accountName === 'newPhoneNumber') {
        // Only allow numbers
        newValue = value.replace(/\D/g, '');
      }
      setAccountSettings(prev => ({
        ...prev,
        [accountName]: newValue
      }));
    } else if (name.startsWith('filter_')) {
      const filterName = name.replace('filter_', '');
      setHistoryFilters(prev => ({
        ...prev,
        [filterName]: value
      }));
      setCurrentPage(1); // Reset to first page when filters change
    } else {
      // Handle formState fields (trading configuration)
      let newValue;
      if (type === 'checkbox') {
        // For checkboxes, use the 'checked' property
        newValue = checked;
      } else if (name === 'phoneNumber') {
        // Only allow numbers
        newValue = value.replace(/\D/g, '');
      } else if (name === 'tradierToken') {
        if (!showToken && formState.tradierToken && !isBulletPlaceholder(value) && !isMaskedCredential(value)) {
          setShowToken(true);
        }
        if (isMaskedCredential(value) || isBulletPlaceholder(value)) {
          return;
        }
        newValue = value;
      } else if (type === 'number') {
        // Handle number inputs
        newValue = value === '' ? '' : parseFloat(value);
      } else {
        newValue = value;
      }
      setFormState(prev => ({
        ...prev,
        [name]: newValue
      }));
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    if (formState.password !== formState.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    const formattedPhoneNumber = formatPhoneToE164(formState.phoneNumber);
    if (!formattedPhoneNumber) {
      setError(
        'A valid mobile phone number is required (10 digits for US, or include country code for international).'
      );
      setLoading(false);
      return;
    }

    try {
      devLog('Starting user registration...');
      const skipMeta = getWhopSkipClientMetadata();
      const email = normalizeUsername(formState.email);
      const { user: cognitoUser } = await signUp({
        username: email,
        password: formState.password,
        options: {
          userAttributes: {
            email,
            name: email.split('@')[0],
            phone_number: formattedPhoneNumber,
          },
          ...(skipMeta && { clientMetadata: skipMeta })
        }
      });

      devLog('User registered successfully:', cognitoUser);
      setMessage('Account created! Please check your email for the verification code. You must verify your email before logging in:');
      setIsVerifying(true);  // Show verification form
      setResendCooldownRemaining(RESEND_VERIFICATION_COOLDOWN_SEC);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const leaveForgotPasswordFlow = () => {
    setForgotPasswordView(null);
    setPasswordResetCode('');
    setPasswordResetResendCooldownRemaining(0);
    setError('');
    setMessage('');
    setFormState((prev) => ({ ...prev, password: '', confirmPassword: '' }));
  };

  const handleForgotPasswordSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    const username = normalizeUsername(formState.email);
    if (!username) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    try {
      await resetPassword({ username });
      setMessage(
        'If an account exists for this email, we sent a verification code. Enter the code below and choose a new password.'
      );
      setForgotPasswordView('set_password');
      setPasswordResetResendCooldownRemaining(PASSWORD_RESET_RESEND_COOLDOWN_SEC);
    } catch (err) {
      setError(err.message || 'Could not send reset code. Try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendPasswordResetCode = async () => {
    if (passwordResetResendCooldownRemaining > 0) return;
    const username = normalizeUsername(formState.email);
    if (!username) return;
    setPasswordResetResendLoading(true);
    setError('');
    try {
      await resetPassword({ username });
      setMessage('If an account exists for this email, we sent a new verification code.');
      setPasswordResetResendCooldownRemaining(PASSWORD_RESET_RESEND_COOLDOWN_SEC);
    } catch (err) {
      setError(err.message || 'Could not resend code.');
    } finally {
      setPasswordResetResendLoading(false);
    }
  };

  const handleForgotPasswordConfirmNewPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    const username = normalizeUsername(formState.email);
    const v = validateResetConfirmFields({
      confirmationCode: passwordResetCode,
      password: formState.password,
      confirmPassword: formState.confirmPassword,
    });
    if (!v.ok) {
      setError(v.error);
      return;
    }
    if (!username) {
      setError('Email is missing. Go back and enter your email again.');
      return;
    }
    setLoading(true);
    try {
      await confirmResetPassword({
        username,
        confirmationCode: passwordResetCode.trim(),
        newPassword: formState.password,
      });
      leaveForgotPasswordFlow();
      setIsLoginView(true);
      setMessage('Password updated. You can log in with your new password.');
    } catch (err) {
      setError(err.message || 'Could not reset password.');
    } finally {
      setLoading(false);
    }
  };

  // SMS verification handler (to be implemented later)
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const skipMeta = getWhopSkipClientMetadata();
      const email = normalizeUsername(formState.email);
      await confirmSignUp({
        username: email,
        confirmationCode: verificationCode,
        options: {
          ...(skipMeta && { clientMetadata: skipMeta })
        }
      });

      // After verification, sign in automatically with robust token handling
      devLog('Attempting to sign in after verification with:', email);

      const signInResult = await signIn({
        username: email,
        password: formState.password
      });
      devLog('Full signIn result after verification:', signInResult);

      // In Amplify v6, signIn might return the session directly or in a different structure
      let tokens;
      if (signInResult.tokens) {
        tokens = signInResult.tokens;
      } else if (signInResult.signInOutput?.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // Handle new password requirement
        throw new Error('New password required. Please check your email for temporary password.');
      } else {
        // Try to get tokens from the session
        const session = await fetchAuthSession();
        tokens = session.tokens;
      }

      devLog('Login response tokens after verification:', tokens);

      if (!tokens) {
        console.error('No tokens received from signIn after verification');
        throw new Error('Authentication failed - no tokens received after verification');
      }

      const attributes = extractUserAttributes(tokens);
      if (!attributes) {
        throw new Error('No valid user attributes received after verification');
      }

      const currentUser = { attributes: { email: attributes.email } };
      setUser(currentUser);

      // Verify session is properly established
      devLog('Verifying session after verification login...');
      const verifySession = await fetchAuthSession();
      devLog('Session verification after verification:', verifySession);

      await loadUserData(currentUser);
      await loadUserSettings(currentUser);

      // Clear form and verification states
      setFormState({
        email: '',
        password: '',
        confirmPassword: '',
        tradierToken: '',
        discordUserId: '',
        phoneNumber: '',
      });
      setVerificationCode('');
      setIsVerifying(false);
      setTempUser(null);
      setMessage('Account verified and logged in successfully!');
    } catch (error) {
      if (isDisabledUserError(error)) {
        setError(WHOP_ACCESS_DISABLED_MESSAGE);
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Resend verification code (with cooldown to respect Cognito limits)
  const handleResendCode = async (e) => {
    e.preventDefault();
    if (resendCooldownRemaining > 0 || resendLoading || !formState.email) return;
    setError('');
    setMessage('');
    setResendLoading(true);
    try {
      await resendSignUpCode({ username: normalizeUsername(formState.email) });
      setMessage('A new verification code has been sent to your email. Please check your inbox (and spam folder).');
      setResendCooldownRemaining(RESEND_VERIFICATION_COOLDOWN_SEC);
    } catch (err) {
      console.error('Resend verification code error:', err);
      setError(err.message || 'Could not send a new code. Please try again in a minute.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    const email = normalizeUsername(formState.email);

    try {
      // Check if there's already a signed-in user
      const currentSession = await fetchAuthSession();
      if (currentSession.tokens) {
        devLog('User already signed in, signing out first...');
        await forceClearAuth();
        // Wait a moment for the signout to complete
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      devLog('Attempting to sign in with:', email);
      devLog('SignIn parameters:', { username: email, password: '[REDACTED]' });

      const signInResult = await signIn({ username: email, password: formState.password });
      devLog('Full signIn result:', signInResult);

      // In Amplify v6, signIn might return the session directly or in a different structure
      let tokens;
      if (signInResult.tokens) {
        tokens = signInResult.tokens;
      } else if (signInResult.signInOutput?.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // Handle new password requirement
        throw new Error('New password required. Please check your email for temporary password.');
      } else {
        // Try to get tokens from the session
        const session = await fetchAuthSession();
        tokens = session.tokens;
      }

      devLog('Login response tokens:', tokens);

      if (!tokens) {
        console.error('No tokens received from signIn');
        throw new Error('Authentication failed - no tokens received');
      }

      const attributes = extractUserAttributes(tokens);
      if (!attributes) {
        throw new Error('No valid user attributes received after login');
      }

      const currentUser = { attributes: { email: attributes.email } };
      markActiveUserEmail(attributes.email);
      setUser(currentUser);

      // Verify session is properly established
      devLog('Verifying session after login...');
      const verifySession = await fetchAuthSession();
      devLog('Session verification:', verifySession);

      await loadUserData(currentUser);
      await loadUserSettings(currentUser);

      // Check if user is admin via Cognito groups (only once per session)
      if (!adminChecked) {
      const session2 = await fetchAuthSession();
      const groups2 = session2?.tokens?.idToken?.payload?.['cognito:groups'] || [];
      if (userHasAdminGroup(groups2)) {
        devLog('Setting admin user state...');
        setIsAdmin(true);
        setAdminUsers([]);
        }
        setAdminChecked(true);
      }

      setMessage('');
      setFormState(createInitialFormState());
    } catch (error) {
      console.error('Login error:', error);

      // Handle specific error cases
      if (error.message && error.message.includes('already signed in')) {
        setError('There is already a signed in user. Please try logging out first, or refresh the page.');
        // Offer to force clear the auth state
        setMessage('Click "Clear Session" to reset authentication state');
      } else if ((error.name && error.name.toLowerCase().includes('usernotconfirmed')) ||
                 (error.message && error.message.toLowerCase().includes('not confirmed'))) {
        // User exists but has not verified their email yet
        try {
          devLog('User not confirmed. Resending verification code for:', email);
          await resendSignUpCode({ username: email });
          setIsVerifying(true);
          setResendCooldownRemaining(RESEND_VERIFICATION_COOLDOWN_SEC);
          setError('');
          setMessage('Your account exists but is not verified yet. We have sent a new verification code to your email. Please enter it below to complete signup.');
        } catch (resendError) {
          console.error('Error resending verification code:', resendError);
          setError('Your account is not verified yet and we were unable to resend the verification code automatically. Please contact support or try again later.');
        }
      } else if (isDisabledUserError(error)) {
        setError(WHOP_ACCESS_DISABLED_MESSAGE);
      } else {
        setError(error.message || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      activeUserEmailRef.current = null;
      resetAllUserState();
      setUser(null);
      setUserData(null);
      setIsAdmin(false);
      setAdminChecked(false);
      setTokenStatusChecked(false);
      setCurrentView('dashboard');
      devLog('User logged out successfully');
    }
  };

  // Force clear authentication state
  const forceClearAuth = async () => {
    try {
      devLog('Force clearing authentication state...');
      await signOut();
    } catch (error) {
      console.error("Error clearing auth state:", error);
    } finally {
      activeUserEmailRef.current = null;
      resetAllUserState();
      setUser(null);
      setUserData(null);
      setIsAdmin(false);
      setAdminChecked(false);
      setTokenStatusChecked(false);
      setCurrentView('dashboard');
      devLog('Authentication state cleared');
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      devLog('Saving user settings:', userSettings);

      // Call the backend API to store settings
      const authHeaders = await buildAuthHeaders();
      const formData = new URLSearchParams();
      Object.keys(userSettings).forEach(key => {
        formData.append(key, userSettings[key]);
      });

      const response = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': authHeaders.Authorization || '',
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData.toString()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      devLog('Settings saved response:', data);
      setMessage('Settings saved successfully!');
      await refreshConsentStatus({ suppressConsentError: true });
      setCurrentView('dashboard');
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Error saving settings: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    if (accountSettings.newPassword !== accountSettings.confirmNewPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (accountSettings.newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      // Use backend endpoint to set new password
      const authHeaders = await buildAuthHeaders();
      const formData = new URLSearchParams();
      formData.append('newPassword', accountSettings.newPassword);

      const response = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/password`, {
        method: 'PUT',
        headers: {
          'Authorization': authHeaders.Authorization || '',
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData.toString()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setMessage(data?.message || 'Password updated successfully!');
      setError('');
      setAccountSettings(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      }));
    } catch (error) {
      console.error('Password update error:', error);
      setError('Failed to update password: ' + (error?.message || 'Unknown error'));
      setMessage('');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePhoneNumber = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      // Format phone number to E.164 format
      let formattedPhoneNumber;
      formattedPhoneNumber = formatPhoneToE164(accountSettings.newPhoneNumber);
      if (!formattedPhoneNumber) {
        setError('Enter a valid phone number (10 digits for US, or include country code).');
        setLoading(false);
        return;
      }

      // Use backend endpoint to update phone
      const authHeaders = await buildAuthHeaders();
      const formData = new URLSearchParams();
      formData.append('phoneNumber', formattedPhoneNumber);

      const response = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/phone`, {
        method: 'PUT',
        headers: {
          'Authorization': authHeaders.Authorization || '',
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData.toString()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setMessage(data?.message || 'Phone number updated successfully!');
      setError('');
      setAccountSettings(prev => ({
        ...prev,
        newPhoneNumber: ''
      }));

      // Refresh user data to show updated phone number
      await loadUserData(user);
    } catch (error) {
      console.error('Phone number update error:', error);
      setError('Failed to update phone number: ' + (error?.message || 'Unknown error'));
      setMessage('');
    } finally {
      setLoading(false);
    }
  };

  // Validation functions
  const validateDiscordUserId = async (userId) => {
    try {
      setValidationState(prev => ({
        ...prev,
        discordUserId: { status: 'validating', message: 'Validating Discord user...' }
      }));

      // Basic Discord ID format validation (should be 17-19 digits)
      if (!userId || !/^\d{17,19}$/.test(userId)) {
        setValidationState(prev => ({
          ...prev,
          discordUserId: { status: 'invalid', message: 'Invalid Discord ID format (should be 17-19 digits)' }
        }));
        return false;
      }

      // Build auth headers
      const authHeaders = await buildAuthHeaders();

      if (!authHeaders.Authorization) {
        console.error('No authorization token available');
        setValidationState(prev => ({
          ...prev,
          discordUserId: { status: 'error', message: 'Authentication required. Please log in again.' }
        }));
        return false;
      }

      devLog('Calling validation API with auth header:', authHeaders.Authorization ? 'present' : 'missing');

      // Call our validation API to check if Discord user exists
      const response = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/validation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeaders.Authorization
        },
        body: JSON.stringify({
          type: 'discord',
          userId: userId
        })
      });

      if (!response.ok) {
        // Try to parse error response
        let errorMessage = `Discord validation failed (HTTP ${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (data.valid) {
        setValidationState(prev => ({
          ...prev,
          discordUserId: { status: 'valid', message: data.message }
        }));
        return true;
      } else {
        setValidationState(prev => ({
          ...prev,
          discordUserId: { status: 'invalid', message: `Discord validation failed: ${data.message || 'Invalid Discord user ID'}` }
        }));
        return false;
      }
    } catch (error) {
      console.error('Error validating Discord user:', error);
      const errorMessage = error.message || 'Error validating Discord ID';
      setValidationState(prev => ({
        ...prev,
        discordUserId: { status: 'error', message: `Discord validation error: ${errorMessage}` }
      }));
      return false;
    }
  };

  const validateTradierCredentials = async (token, accountId) => {
    try {
      setValidationState(prev => ({
        ...prev,
        tradierAccountId: { status: 'validating', message: 'Validating Tradier account...' },
        tradierToken: { status: 'validating', message: 'Validating Tradier token...' }
      }));

      // Basic format validation first
      if (!token || !/^[A-Za-z0-9]{20,40}$/.test(token)) {
        setValidationState(prev => ({
          ...prev,
          tradierToken: { status: 'invalid', message: 'Invalid token format (should be 20-40 alphanumeric characters)' }
        }));
        return false;
      }

      if (!accountId || !/^[A-Z0-9]{6,10}$/.test(accountId)) {
        setValidationState(prev => ({
          ...prev,
          tradierAccountId: { status: 'invalid', message: 'Invalid account ID format (should be 6-10 alphanumeric characters)' }
        }));
        return false;
      }

      // Build auth headers
      const authHeaders = await buildAuthHeaders();

      if (!authHeaders.Authorization) {
        console.error('No authorization token available');
        setValidationState(prev => ({
          ...prev,
          tradierAccountId: { status: 'error', message: 'Authentication required. Please log in again.' },
          tradierToken: { status: 'error', message: 'Authentication required. Please log in again.' }
        }));
        return false;
      }

      devLog('Calling validation API with auth header:', authHeaders.Authorization ? 'present' : 'missing');

      // Call our validation API to check if Tradier credentials are valid
      const response = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/validation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeaders.Authorization
        },
        body: JSON.stringify({
          type: 'tradier',
          token: token,
          accountId: accountId,
          useSandbox: formState.tradierUseSandbox || false
        })
      });

      if (!response.ok) {
        // Try to parse error response
        let errorMessage = `Tradier validation failed (HTTP ${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (data.valid) {
        setValidationState(prev => ({
          ...prev,
          tradierAccountId: { status: 'valid', message: 'Tradier account validated successfully' },
          tradierToken: { status: 'valid', message: 'Tradier token validated successfully' }
        }));
        return true;
      } else {
        const errorMessage = data.message || 'Invalid Tradier credentials';
        const displayMessage = data.code === 'ACCOUNT_ID_ALREADY_IN_USE'
          ? errorMessage
          : `Tradier validation failed: ${errorMessage}`;
        setValidationState(prev => ({
          ...prev,
          tradierAccountId: { status: 'invalid', message: displayMessage },
          tradierToken: { status: 'invalid', message: displayMessage }
        }));
        return false;
      }
    } catch (error) {
      console.error('Error validating Tradier credentials:', error);
      const errorMessage = error.message || 'Error validating Tradier credentials';
      setValidationState(prev => ({
        ...prev,
        tradierAccountId: { status: 'error', message: `Tradier validation error: ${errorMessage}` },
        tradierToken: { status: 'error', message: `Tradier validation error: ${errorMessage}` }
      }));
      return false;
    }
  };

  // Channel mapping validation (same logic as Trading Config, updates mappingValidationState)
  const validateMappingDiscordUserId = async (userId) => {
    try {
      setMappingValidationState(prev => ({
        ...prev,
        discordUserId: { status: 'validating', message: 'Validating Discord user...' }
      }));
      if (!userId || !/^\d{17,19}$/.test(userId)) {
        setMappingValidationState(prev => ({
          ...prev,
          discordUserId: { status: 'invalid', message: 'Invalid Discord ID format (should be 17-19 digits)' }
        }));
        return false;
      }
      const authHeaders = await buildAuthHeaders();
      if (!authHeaders.Authorization) {
        setMappingValidationState(prev => ({
          ...prev,
          discordUserId: { status: 'error', message: 'Authentication required.' }
        }));
        return false;
      }
      const baseUrl = import.meta.env.VITE_API_URL || getApiEndpoint();
      const response = await fetch(`${baseUrl}/validation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': authHeaders.Authorization },
        body: JSON.stringify({ type: 'discord', userId })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }
      const data = await response.json();
      if (data.valid) {
        setMappingValidationState(prev => ({
          ...prev,
          discordUserId: { status: 'valid', message: data.message || 'Discord ID validated' }
        }));
        return true;
      }
      setMappingValidationState(prev => ({
        ...prev,
        discordUserId: { status: 'invalid', message: data.message || 'Invalid Discord user ID' }
      }));
      return false;
    } catch (err) {
      setMappingValidationState(prev => ({
        ...prev,
        discordUserId: { status: 'error', message: err.message || 'Error validating Discord ID' }
      }));
      return false;
    }
  };

  const validateMappingTradierCredentials = async (token, accountId, useSandbox) => {
    try {
      setMappingValidationState(prev => ({
        ...prev,
        accountId: { status: 'validating', message: 'Validating account...' },
        apiToken: { status: 'validating', message: 'Validating token...' }
      }));
      if (!token || !/^[A-Za-z0-9]{20,40}$/.test(token)) {
        setMappingValidationState(prev => ({
          ...prev,
          apiToken: { status: 'invalid', message: 'Invalid token format (20-40 alphanumeric characters)' }
        }));
        return false;
      }
      if (!accountId || !/^[A-Z0-9]{6,10}$/.test(accountId)) {
        setMappingValidationState(prev => ({
          ...prev,
          accountId: { status: 'invalid', message: 'Invalid account ID format (6-10 alphanumeric characters)' }
        }));
        return false;
      }
      const authHeaders = await buildAuthHeaders();
      if (!authHeaders.Authorization) {
        setMappingValidationState(prev => ({
          ...prev,
          accountId: { status: 'error', message: 'Authentication required.' },
          apiToken: { status: 'error', message: 'Authentication required.' }
        }));
        return false;
      }
      const baseUrl = import.meta.env.VITE_API_URL || getApiEndpoint();
      const response = await fetch(`${baseUrl}/validation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': authHeaders.Authorization },
        body: JSON.stringify({ type: 'tradier', token, accountId, useSandbox: useSandbox || false })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }
      const data = await response.json();
      if (data.valid) {
        setMappingValidationState(prev => ({
          ...prev,
          accountId: { status: 'valid', message: 'Account validated' },
          apiToken: { status: 'valid', message: 'Token validated' }
        }));
        return true;
      }
      const msg = data.message || 'Invalid credentials';
      setMappingValidationState(prev => ({
        ...prev,
        accountId: { status: 'invalid', message: msg },
        apiToken: { status: 'invalid', message: msg }
      }));
      return false;
    } catch (err) {
      const msg = err.message || 'Validation error';
      setMappingValidationState(prev => ({
        ...prev,
        accountId: { status: 'error', message: msg },
        apiToken: { status: 'error', message: msg }
      }));
      return false;
    }
  };

  const handleValidateMapping = async (e) => {
    e.preventDefault();
    setValidatingMapping(true);
    setMappingValidated(false);
    setError('');
    setMessage('');
    setMappingValidationState({
      apiToken: { status: 'pending', message: '' },
      accountId: { status: 'pending', message: '' },
      discordUserId: { status: 'pending', message: '' }
    });
    try {
      const discordValid = await validateMappingDiscordUserId(mappingFormState.discord_user_id);
      let platformValid = true;
      const skipMappingTokenCheck =
        hasMappingApiToken && isMaskedCredential(mappingFormState.api_token);
      if (mappingFormState.trading_platform === 'tradier') {
        platformValid = skipMappingTokenCheck
          ? true
          : await validateMappingTradierCredentials(
              mappingFormState.api_token,
              mappingFormState.account_id,
              mappingFormState.use_sandbox
            );
        if (skipMappingTokenCheck) {
          setMappingValidationState((prev) => ({
            ...prev,
            apiToken: { status: 'valid', message: 'Using saved API token' },
          }));
        }
      } else {
        setMappingValidationState(prev => ({
          ...prev,
          apiToken: { status: 'pending', message: 'IBKR/Schwab validation coming soon' },
          accountId: { status: 'pending', message: '' }
        }));
      }
      if (discordValid && platformValid) {
        setMappingValidated(true);
      } else {
        const parts = [];
        if (!discordValid) parts.push('Discord User ID (17–19 digits, or validation failed)');
        if (!platformValid && mappingFormState.trading_platform === 'tradier') parts.push('API token / Account ID (format or Tradier check failed)');
        const detail = parts.length ? ': ' + parts.join('; ') : '. Check the red validation messages above.';
        setError('Validation failed. Fix the following and click Validate again' + detail);
      }
    } finally {
      setValidatingMapping(false);
    }
  };

  const handleValidate = async (e) => {
    e.preventDefault();
    setValidating(true);
    setIsValidated(false);
    setError('');
    setMessage('');

    try {
      // Reset validation state
      setValidationState({
        discordUserId: { status: 'validating', message: 'Validating Discord user...' },
        tradierAccountId: { status: 'validating', message: 'Validating Tradier account...' },
        tradierToken: { status: 'validating', message: 'Validating Tradier token...' }
      });

      // Validate Discord user and Tradier credentials in parallel
      const skipTradierTokenCheck = hasTradierToken && isMaskedCredential(formState.tradierToken);
      const [discordValid, tradierValid] = await Promise.all([
        validateDiscordUserId(formState.discordUserId),
        skipTradierTokenCheck
          ? Promise.resolve(true)
          : validateTradierCredentials(formState.tradierToken, formState.tradierAccountId),
      ]);

      // If all validations pass, show success state (inline Validation Success Summary only)
      if (discordValid && tradierValid) {
        setValidationState({
          discordUserId: { status: 'valid', message: 'Discord ID validated successfully' },
          tradierAccountId: { status: 'valid', message: 'Tradier account validated successfully' },
          tradierToken: {
            status: 'valid',
            message: skipTradierTokenCheck
              ? 'Using saved Tradier token'
              : 'Tradier token validated successfully',
          },
        });
        setIsValidated(true);
        return true;
      } else {
        setIsValidated(false);
        setError('Validation failed. Please fix the errors above and try again.');
        return false;
      }
    } catch (error) {
      console.error('Error during validation:', error);
      setIsValidated(false);
      setError('Error during validation: ' + error.message);
      return false;
    } finally {
      setValidating(false);
    }
  };

  const validateAllFields = async () => {
    // This is kept for backward compatibility but now just calls handleValidate logic
    setValidating(true);
    try {
      setValidationState({
        discordUserId: { status: 'validating', message: 'Validating Discord user...' },
        tradierAccountId: { status: 'validating', message: 'Validating Tradier account...' },
        tradierToken: { status: 'validating', message: 'Validating Tradier token...' }
      });

      const skipTradierTokenCheck = hasTradierToken && isMaskedCredential(formState.tradierToken);
      const [discordValid, tradierValid] = await Promise.all([
        validateDiscordUserId(formState.discordUserId),
        skipTradierTokenCheck
          ? Promise.resolve(true)
          : validateTradierCredentials(formState.tradierToken, formState.tradierAccountId),
      ]);

      if (discordValid && tradierValid) {
        setValidationState({
          discordUserId: { status: 'valid', message: 'Discord ID validated successfully' },
          tradierAccountId: { status: 'valid', message: 'Tradier account validated successfully' },
          tradierToken: {
            status: 'valid',
            message: skipTradierTokenCheck
              ? 'Using saved Tradier token'
              : 'Tradier token validated successfully',
          },
        });
        return true;
      }

      return false;
    } finally {
      setValidating(false);
    }
  };

  const handleSaveConfiguration = async (e) => {
    e.preventDefault();

    // Check if validation has passed
    if (!isValidated) {
      setError('Please validate your credentials first by clicking the "Validate" button');
      return;
    }

    setLoading(true);
    setError('');

    try {
      devLog('Saving configuration...');

      // Build auth headers
      const authHeaders = await buildAuthHeaders();

      // Convert data to form-encoded format to avoid CORS preflight
      const formData = new URLSearchParams();
      if (shouldSendCredentialOnSave(formState.tradierToken)) {
        formData.append('tradierToken', formState.tradierToken);
      }
      formData.append('tradierAccountId', formState.tradierAccountId);
      formData.append('discordUserId', formState.discordUserId);
      // Include risk management settings
      formData.append('priceDeviationLimit', formState.priceDeviationLimit || 10.0);
      formData.append('enablePriceChecks', formState.enablePriceChecks === true ? 'true' : 'false');
      formData.append('maxDailyTrades', formState.maxDailyTrades || DEFAULT_MAX_DAILY_TRADES);
      formData.append('maxSingleTradeAmount', formState.maxSingleTradeAmount || 5000.0);
      formData.append('autoApproveEnabled', formState.autoApproveEnabled === true ? 'true' : 'false');
      formData.append('tradierUseSandbox', formState.tradierUseSandbox === true ? 'true' : 'false');

      // Use fetch directly with form-encoded data to avoid CORS preflight
      const response = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/configuration`, {
        method: 'POST',
        headers: {
          'Authorization': authHeaders.Authorization || '',
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData.toString()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      devLog('Configuration saved response:', data);

      // Update local state to reflect configuration is saved
      setUserData(prev => ({ ...prev, hasTokens: true }));
      setTokenStatusChecked(true);

      // Display success message
      setMessage('Configuration saved successfully!');

      // Show validation warning prominently if present
      if (data.validationWarning) {
        setError(`⚠️ Validation Warning: ${data.validationWarning}`);
      } else {
        setError(''); // Clear any previous errors
      }

      // Reload configuration to get the saved values (including tradierUseSandbox)
      try {
        const authHeaders = await buildAuthHeaders();
        const configResponse = await fetch(`${import.meta.env.VITE_API_URL || getApiEndpoint()}/users/configuration`, {
          method: 'GET',
          headers: {
            'Authorization': authHeaders.Authorization || ''
          }
        });

        if (configResponse.ok) {
          const configData = await configResponse.json();
          const cfg = configData?.configuration ?? configData;
          if (cfg) {
            devLog('🔄 Reloading config after save:', cfg);
            devLog('🔄 tradierUseSandbox from backend:', cfg.tradierUseSandbox, 'type:', typeof cfg.tradierUseSandbox);
            devLog('🔄 tradier_use_sandbox from backend:', cfg.tradier_use_sandbox, 'type:', typeof cfg.tradier_use_sandbox);
            setHasTradierToken(Boolean(cfg.hasTradierToken ?? cfg.tradierToken));
            setFormState(prev => ({
              ...prev,
              tradierToken: cfg.tradierToken || '',
              tradierAccountId: cfg.tradierAccountId || cfg.tradier_account_id || prev.tradierAccountId,
              discordUserId: cfg.discordUserId || cfg.discord_user_id || prev.discordUserId,
              tradierUseSandbox: cfg.tradierUseSandbox !== undefined ? Boolean(cfg.tradierUseSandbox) : (cfg.tradier_use_sandbox !== undefined ? Boolean(cfg.tradier_use_sandbox) : prev.tradierUseSandbox),
              // Also reload risk management settings
              priceDeviationLimit: cfg.priceDeviationLimit !== undefined ? Number(cfg.priceDeviationLimit) : prev.priceDeviationLimit,
              enablePriceChecks: cfg.enablePriceChecks !== undefined ? Boolean(cfg.enablePriceChecks) : prev.enablePriceChecks,
              maxDailyTrades: cfg.maxDailyTrades !== undefined ? Number(cfg.maxDailyTrades) : prev.maxDailyTrades,
              maxSingleTradeAmount: cfg.maxSingleTradeAmount !== undefined ? Number(cfg.maxSingleTradeAmount) : prev.maxSingleTradeAmount,
              maxDailyDollarExposure: cfg.maxDailyDollarExposure !== undefined
                ? Number(cfg.maxDailyDollarExposure)
                : (cfg.maxSingleTradeAmount !== undefined
                  ? Number(cfg.maxSingleTradeAmount)
                  : prev.maxDailyDollarExposure),
              autoApproveEnabled: cfg.autoApproveEnabled !== undefined ? Boolean(cfg.autoApproveEnabled) : prev.autoApproveEnabled,
              positionMultiplier: cfg.positionMultiplier !== undefined ? Number(cfg.positionMultiplier) : prev.positionMultiplier,
            }));
            // Reset show token state when loading new config
            setShowToken(false);
          }
        }
      } catch (reloadError) {
        console.error('Error reloading configuration after save:', reloadError);
        // If reload fails, at least clear the sensitive fields
        setFormState(prev => ({
          ...prev,
          tradierToken: '',
          tradierAccountId: '',
          discordUserId: ''
          // Keep tradierUseSandbox value
        }));
      }

      // Reset validation state and validation flag
      setValidationState({
        discordUserId: { status: 'pending', message: '' },
        tradierAccountId: { status: 'pending', message: '' },
        tradierToken: { status: 'pending', message: '' }
      });
      setIsValidated(false);

      // Navigate to dashboard after successful save
      setTimeout(() => {
        setCurrentView('dashboard');
      }, 1500); // Wait 1.5 seconds to show success message
    } catch (error) {
      console.error('Error saving configuration:', error);
      setError('Error saving configuration: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'filled':
        return 'text-green-600 bg-green-100';
      case 'pending':
      case 'open':
        return 'text-yellow-600 bg-yellow-100';
      case 'partially_filled':
        return 'text-blue-600 bg-blue-100';
      case 'cancelled':
      case 'canceled':
      case 'pending_cancel':
        return 'text-orange-600 bg-orange-100';
      case 'failed':
      case 'rejected':
      case 'expired':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getActionColor = (action) => {
    switch (action?.toLowerCase()) {
      case 'buy': return 'text-green-600 bg-green-100';
      case 'sell': return 'text-red-600 bg-red-100';
      case 'bto': return 'text-blue-600 bg-blue-100';
      case 'stc': return 'text-purple-600 bg-purple-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const renderDashboard = () => (
    <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-4xl mx-auto my-8 relative">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
        Welcome to Shingami Trades
        {isAdmin && <span className="block text-lg text-blue-600 font-normal mt-2">👑 Admin User</span>}
      </h2>

      {/* Loading overlay: settings load and/or trade analytics (silent fetch) */}
      {(loadingDashboard || loadingAnalytics) && (
        <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-2xl z-10">
          <div className="text-center">
            <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600 font-medium">
              {loadingAnalytics && !loadingDashboard ? 'Loading trade stats…' : 'Loading dashboard…'}
            </p>
          </div>
        </div>
      )}

      {isAdmin ? (
        // Admin Dashboard - Quick Stats
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Total Users</h3>
              <p className="text-3xl font-bold text-blue-600">{adminUserSummary.totalUsers}</p>
            </div>

            <div className="bg-green-50 p-6 rounded-xl border border-green-200">
              <h3 className="text-lg font-semibold text-green-800 mb-2">Active Users</h3>
              <p className="text-3xl font-bold text-green-600">
                {adminUserSummary.activeUsers}
              </p>
            </div>

            <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
              <h3 className="text-lg font-semibold text-purple-800 mb-2">Configured Users</h3>
              <p className="text-3xl font-bold text-purple-600">
                {adminUserSummary.configuredUsers}
              </p>
              <p className="text-xs text-purple-700 mt-2">At least one channel mapping</p>
            </div>
          </div>

          <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => {
                  setCurrentView('admin');
                  setAdminSubView('users');
                }}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition duration-200 font-medium"
              >
                Manage users
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Regular User Dashboard
        <>
          <div className="mb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-sm text-gray-500">
              Trade counts from your history. Rejected = broker-side only (Discord tap Reject is not stored).
            </p>
            <label className="text-sm text-gray-600 flex items-center gap-2">
              <span>Period</span>
              <select
                value={analyticsPeriod}
                onChange={(e) => setAnalyticsPeriod(e.target.value)}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
              >
                <option value="7d">7 days</option>
                <option value="30d">30 days</option>
                <option value="90d">90 days</option>
                <option value="1y">1 year</option>
              </select>
            </label>
          </div>
          <p className="text-xs text-gray-400 mb-4">{analyticsPeriodLabel(analyticsPeriod)}</p>
          {(() => {
            const ex = tradingAnalytics?.execution_stats;
            const tt = tradingAnalytics?.total_trades;
            const tradesLogged = ex?.total_logged ?? tt ?? 0;
            const missingExecStats =
              !tradingAnalytics?.error && ex == null && typeof tt === 'number' && tt > 0;
            const fmtExecRej = (v) => {
              if (loadingAnalytics) return '…';
              if (tradingAnalytics?.error) return '—';
              if (missingExecStats) return '—';
              return v ?? 0;
            };
            return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-800 mb-1">Trades logged</h3>
              <p className="text-3xl font-bold text-blue-700">
                {loadingAnalytics ? '…' : tradingAnalytics?.error ? '—' : tradesLogged}
              </p>
              <p className="text-xs text-blue-600 mt-2">Rows in trade history for this period</p>
            </div>
            <div className="bg-green-50 p-6 rounded-xl border border-green-200">
              <h3 className="text-lg font-semibold text-green-800 mb-1">Executed</h3>
              <p className="text-3xl font-bold text-green-700">
                {fmtExecRej(ex?.executed)}
              </p>
              <p className="text-xs text-green-600 mt-2">Filled + submitted (broker)</p>
            </div>
            <div className="bg-orange-50 p-6 rounded-xl border border-orange-200">
              <h3 className="text-lg font-semibold text-orange-800 mb-1">Rejected</h3>
              <p className="text-3xl font-bold text-orange-700">
                {fmtExecRej(ex?.rejected)}
              </p>
              <p className="text-xs text-orange-600 mt-2">Broker / execution rejected</p>
            </div>
          </div>
            );
          })()}

          {channelMappings.length > 0 && (
            <div className="mt-8 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Channel settings</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-600">
                      <th className="py-2 pr-4">Channel</th>
                      <th className="py-2 pr-4">Env</th>
                      <th className="py-2 pr-4">Price dev %</th>
                      <th className="py-2 pr-4">Max trades/day</th>
                      <th className="py-2 pr-4">Auto-approve</th>
                      <th className="py-2 pr-4">Account</th>
                    </tr>
                  </thead>
                  <tbody>
                    {channelMappings.map((m) => (
                      <tr key={m.id || m.channel_id} className="border-b border-gray-100">
                        <td className="py-2 pr-4">
                          <span className="font-medium text-gray-900">
                            {m.channel_name ? `#${m.channel_name}` : 'Channel'}
                          </span>
                          <span className="block text-xs text-gray-500 font-mono">{m.channel_id}</span>
                        </td>
                        <td className="py-2 pr-4">{m.use_sandbox ? 'Sandbox' : 'Live'}</td>
                        <td className="py-2 pr-4">{Number(m.price_deviation_limit ?? 10)}%</td>
                        <td className="py-2 pr-4">{m.max_daily_trades ?? DEFAULT_MAX_DAILY_TRADES}</td>
                        <td className="py-2 pr-4">{m.auto_approve_enabled ? 'On' : 'Off'}</td>
                        <td className="py-2 pr-4 font-mono text-xs">{m.account_id || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <DashboardAgreementsSection
            hasAcceptedTerms={hasAcceptedTerms}
            hasAcceptedSms={hasAcceptedSms}
            hasTradingConfig={hasTradingConfig}
            canAccessChannelConfig={canAccessChannelConfig}
            termsReminderMessage={termsReminderMessage}
            smsReminderMessage={smsReminderMessage}
            userKey={user?.email || ''}
            onOpenTerms={() => setCurrentView('agreements_terms')}
            onOpenSms={() => setCurrentView('agreements_sms')}
            onSkipSms={() => setCurrentView('dashboard')}
            onOpenTradingConfig={() => setCurrentView('channel_config')}
          />
        </>
      )}
    </div>
  );

  const renderDocuments = () => {
    const apiBaseUrl = import.meta.env.VITE_API_URL || getApiEndpoint();
    const signedConsentDocs = [
      {
        id: 'terms-consent',
        title: 'Signed consent — Terms',
        isSigned: hasAcceptedTerms,
        url: '',
        action: downloadTermsPackage,
        actionLabel: downloadingTermsPackage ? 'Preparing package...' : 'Download package',
        actionDisabled:
          canDownloadSignedConsentPackages && hasAcceptedTerms
            ? downloadingTermsPackage
            : !canDownloadSignedConsentPackages,
      },
      {
        id: 'sms-consent',
        title: 'Signed consent — SMS',
        isSigned: hasAcceptedSms,
        url: '',
        action: downloadSmsConsentPdf,
        actionLabel: downloadingSmsConsentPdf ? 'Preparing PDF...' : 'Download PDF',
        actionDisabled:
          canDownloadSignedConsentPackages && hasAcceptedSms
            ? downloadingSmsConsentPdf
            : !canDownloadSignedConsentPackages,
      },
    ];

    return (
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Documents</h2>
          <p className="text-sm text-gray-600 mb-6">
            Review disclosures and open your signed consent PDFs when available.
          </p>
          {canAccessDocumentsTab && !canDownloadSignedConsentPackages && (
            <div className="mb-6 rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
              Disclosure documents are available to view. Signed consent package PDFs unlock after completing Terms (Step 1) and Trading configuration (Step 3). The SMS consent PDF is available once you opt in to SMS alerts (optional).
            </div>
          )}
          {canAccessDocumentsTab && consentActionError && (
            <div className="mb-6 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900">
              {consentActionError}
            </div>
          )}

          <DisclosuresSection apiBaseUrl={apiBaseUrl} />

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Signed consent forms</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {signedConsentDocs.map((doc) => (
              <div key={doc.id} className="border rounded-xl p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <span className="text-sm font-medium text-gray-800">{doc.title}</span>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      doc.isSigned ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {doc.isSigned ? 'Signed' : 'Pending'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {doc.url ? (
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                    >
                      View PDF
                    </a>
                  ) : doc.action ? (
                    <button
                      type="button"
                      disabled={doc.actionDisabled}
                      onClick={doc.action}
                      className={`px-3 py-2 rounded-md text-sm font-medium border ${
                        !doc.actionDisabled
                          ? 'border-gray-300 text-gray-700 hover:bg-gray-100'
                          : 'border-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {doc.actionLabel}
                    </button>
                  ) : (
                    <span className="px-3 py-2 rounded-md text-sm font-medium border border-gray-200 text-gray-400">
                      {doc.actionLabel}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderNavigation = () => (
    <div className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-6xl mx-auto px-4 py-3">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-800">Shingami Trades</h1>
            {user && (
              <p className="text-sm text-gray-600 mt-1">
                Logged in as: <span className="font-medium text-blue-600">{user.attributes?.email}</span>
                {isAdmin && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Admin</span>}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2 w-full md:w-auto">
            <button
              onClick={() => setCurrentView('dashboard')}
              className={`px-3 py-2.5 rounded-md text-sm font-medium min-h-[44px] ${
                currentView === 'dashboard' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Dashboard
            </button>
            {isAdmin ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setCurrentView('admin');
                    setAdminSubView('users');
                  }}
                  className={`px-3 py-2.5 rounded-md text-sm font-medium min-h-[44px] ${
                    currentView === 'admin' && adminSubView === 'users'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Manage users
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCurrentView('admin');
                    setAdminSubView('health');
                  }}
                  className={`px-3 py-2.5 rounded-md text-sm font-medium min-h-[44px] ${
                    currentView === 'admin' && adminSubView === 'health'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  System health
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCurrentView('admin');
                    setAdminSubView('logs');
                  }}
                  className={`px-3 py-2.5 rounded-md text-sm font-medium min-h-[44px] ${
                    currentView === 'admin' && adminSubView === 'logs'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Live tail logs
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => canAccessTradeHistory && setCurrentView('trading_history')}
                  disabled={!canAccessTradeHistory}
                  className={navTabClassName(
                    currentView === 'trading_history',
                    canAccessTradeHistory
                  )}
                >
                  Trade History
                </button>
                <button
                  onClick={() => canAccessChannelConfig && setCurrentView('channel_config')}
                  disabled={!canAccessChannelConfig}
                  className={navTabClassName(
                    currentView === 'channel_config',
                    canAccessChannelConfig
                  )}
                >
                  Channel Config
                </button>
                <button
                  type="button"
                  onClick={() => canAccessDocumentsTab && setCurrentView('documents')}
                  disabled={!canAccessDocumentsTab}
                  className={navTabClassName(
                    currentView === 'documents',
                    canAccessDocumentsTab
                  )}
                >
                  Documents
                </button>
              </>
            )}
            {/* Analytics tab temporarily hidden */}
            {/*
            <button
              onClick={() => setCurrentView('trading_analytics')}
              className={`px-3 py-2 rounded-md text-sm font-medium ${
                currentView === 'trading_analytics' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Analytics
            </button>
            */}
            <button
              type="button"
              onClick={() => setCurrentView('help_support')}
              className={`px-3 py-2.5 rounded-md text-sm font-medium min-h-[44px] ${
                currentView === 'help_support' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Help & Support
            </button>
            <button
              onClick={() => setCurrentView('settings')}
              className={`px-3 py-2.5 rounded-md text-sm font-medium min-h-[44px] ${
                currentView === 'settings' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Settings
            </button>
            <button
              onClick={handleLogout}
              className="px-3 py-2.5 rounded-md text-sm font-medium text-red-600 hover:text-red-700 min-h-[44px]"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTradeHistory = () => (
    <div className="max-w-7xl mx-auto p-4 md:p-8">
      <div className="bg-white rounded-2xl shadow-xl p-4 md:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800">Trade History</h2>
          <button
            onClick={loadTradeHistory}
            className="bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition duration-200 min-h-[44px] w-full sm:w-auto"
          >
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="bg-gray-50 p-6 rounded-xl mb-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Symbol</label>
              <input
                type="text"
                name="filter_symbol"
                value={historyFilters.symbol}
                onChange={handleInputChange}
                placeholder="e.g., AAPL"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Action</label>
              <select
                name="filter_action"
                value={historyFilters.action}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Actions</option>
                <option value="buy">Buy</option>
                <option value="sell">Sell</option>
                <option value="bto">BTO</option>
                <option value="stc">STC</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Status</label>
              <select
                name="filter_status"
                value={historyFilters.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="open">Open</option>
                <option value="partially_filled">Partially Filled</option>
                <option value="filled">Filled</option>
                <option value="expired">Expired</option>
                <option value="canceled">Canceled</option>
                <option value="rejected">Rejected</option>
                <option value="pending_cancel">Pending Cancel</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Trade Type</label>
              <select
                name="filter_trade_type"
                value={historyFilters.trade_type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="option">Options</option>
                <option value="equity">Equity</option>
                <option value="stock">Stock</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Environment</label>
              <select
                name="filter_environment"
                value={historyFilters.environment}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Environments</option>
                <option value="production">Production</option>
                <option value="sandbox">Sandbox</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Channel</label>
              <select
                name="filter_channel_id"
                value={historyFilters.channel_id}
                onChange={handleInputChange}
                data-testid="trade-history-channel-filter"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Channels</option>
                {channelMappings.map((mapping) => (
                  <option key={mapping.id || mapping.channel_id} value={mapping.channel_id}>
                    {mapping.channel_name || `Channel ${mapping.channel_id}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Trading Platform</label>
              <select
                name="filter_trading_platform"
                value={historyFilters.trading_platform}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Platforms</option>
                <option value="tradier">Tradier</option>
                <option value="ibkr">IBKR</option>
                <option value="schwab">Schwab</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Sort By</label>
              <select
                name="filter_sort_by"
                value={historyFilters.sort_by}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="created_at">Date</option>
                <option value="symbol">Symbol</option>
                <option value="total_amount">Amount</option>
              </select>
            </div>
          </div>
        </div>

        {/* Trade Table */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading trade history...</p>
          </div>
        ) : tradeHistory.length === 0 ? (
          <div className="text-center py-8">
            {tradeHistoryError ? (
              <>
                <p className="text-amber-600 font-medium">{tradeHistoryError}</p>
                <p className="text-gray-500 mt-2 text-sm">If you expected trades, check that your account is linked and the database is reachable.</p>
              </>
            ) : (
              <p className="text-gray-500">No trades found. Start trading to see your history here.</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto -mx-4 md:mx-0">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Symbol</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Channel</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Environment</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tradeHistory.map((trade, index) => (
                  <tr key={trade.id || index} className="hover:bg-gray-50">
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(trade.created_at)}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {trade.symbol}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(trade.action)}`}>
                        {trade.action?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {trade.quantity}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(trade.price != null && trade.price !== '') ? formatCurrency(trade.price) : '—'}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(trade.total_amount)}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(trade.status)}`}>
                        {trade.status}
                      </span>
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {trade.channel_name || (trade.channel_id ? `Channel ${trade.channel_id}` : '—')}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        trade.trading_platform === 'tradier' ? 'bg-blue-100 text-blue-800' :
                        trade.trading_platform === 'ibkr' ? 'bg-purple-100 text-purple-800' :
                        trade.trading_platform === 'schwab' ? 'bg-indigo-100 text-indigo-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {(trade.trading_platform || 'tradier').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        (trade.api_environment === 'sandbox' || trade.tradier_use_sandbox)
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {(trade.api_environment === 'sandbox' || trade.tradier_use_sandbox) ? 'Sandbox' : 'Production'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // Platform-specific label mapping
  const getPlatformLabels = (platform) => {
    const labels = {
      tradier: {
        accountId: 'Tradier Account ID',
        token: 'Tradier API Token',
        sandboxLabel: 'Use Sandbox API',
        sandboxHelp: 'Check this if your Tradier token is for the sandbox environment (https://sandbox.tradier.com/v1/)'
      },
      ibkr: {
        accountId: 'IBKR Account ID',
        token: 'IBKR API Key',
        sandboxLabel: 'Use Paper Trading',
        sandboxHelp: 'Check this to use IBKR paper trading account instead of live trading.'
      },
      schwab: {
        accountId: 'Schwab Account ID',
        token: 'Schwab API Token',
        sandboxLabel: 'Use Paper Trading',
        sandboxHelp: 'Check this to use Schwab paper trading (simulation) instead of live.'
      }
    };
    return labels[platform] || labels.tradier;
  };

  const renderChannelConfig = () => (
    <div className="max-w-7xl mx-auto p-4 md:p-8">
      <div className="bg-white rounded-2xl shadow-xl p-4 md:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800">Channel Configuration</h2>
            <p className="text-gray-600 mt-1">Configure trading accounts for different Discord channels</p>
          </div>
          <button
            onClick={() => {
              setSelectedMapping(null);
              setMessage('');
              setError('');
      setShowMappingForm(true);
      setMappingFormState(createDefaultMappingFormState());
      setMappingValidationState({
        apiToken: { status: 'pending', message: '' },
        accountId: { status: 'pending', message: '' },
        discordUserId: { status: 'pending', message: '' }
      });
      setMappingValidated(false);
      setManualChannelEntry(false);
      loadAvailableChannels();
      loadAvailablePlatforms();
            }}
            className="bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition duration-200 min-h-[44px] flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Channel Mapping
          </button>
        </div>

        {/* Manage channels: admin-only */}
        {isAdmin && (
        <div className="border-t pt-6 mt-6">
          <h3 className="text-xl font-bold text-gray-800 mb-3">Manage Channels</h3>
          <p className="text-gray-600 text-sm mb-4">Edit display name and server (guild) name for Discord channels. These values are used in the app only; the real channel ID does not change.</p>
          {availableChannels.length === 0 ? (
            <p className="text-gray-500 text-sm">No channels loaded. Run discovery or add a channel mapping to see channels here.</p>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-xl p-3 bg-gray-50">
                {availableChannels.map((ch) => (
                  <div key={ch.channel_id} className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-gray-100">
                    <div>
                      <span className="font-medium text-gray-800">{ch.channel_name || ch.channel_id}</span>
                      <span className="text-gray-500 text-sm ml-2">({ch.guild_name || '—'})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedChannelForEdit(ch);
                        setChannelEditForm({ channel_name: ch.channel_name || '', guild_name: ch.guild_name || '' });
                        loadChannelDetails(ch.channel_id);
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                    >
                      Edit
                    </button>
                  </div>
                ))}
              </div>
              {selectedChannelForEdit && (
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                  <h4 className="font-semibold text-gray-800 mb-3">Edit channel: {selectedChannelForEdit.channel_name || selectedChannelForEdit.channel_id}</h4>
                  {loadingChannelDetails ? (
                    <p className="text-gray-500 text-sm">Loading details...</p>
                  ) : (
                    <div className="space-y-3 max-w-md">
                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-1">Channel name (display)</label>
                        <input
                          type="text"
                          value={channelEditForm.channel_name}
                          onChange={(e) => setChannelEditForm(prev => ({ ...prev, channel_name: e.target.value }))}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g. 10k-challenge"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-1">Guild / server name (display)</label>
                        <input
                          type="text"
                          value={channelEditForm.guild_name}
                          onChange={(e) => setChannelEditForm(prev => ({ ...prev, guild_name: e.target.value }))}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g. Trading Server"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => saveChannelEdit(selectedChannelForEdit.channel_id)}
                          disabled={savingChannelEdit}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                        >
                          {savingChannelEdit ? 'Saving...' : 'Save channel'}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setSelectedChannelForEdit(null); setChannelEditForm({ channel_name: '', guild_name: '' }); setError(''); }}
                          className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 text-sm font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {/* Inline form: same layout as Trading Config */}
        {showMappingForm && (
        <div className="bg-white p-4 md:p-8 rounded-2xl shadow-xl w-full max-w-2xl mx-auto my-4 md:my-8 border border-gray-100">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-6">
            {selectedMapping ? 'Edit Channel Mapping' : 'Add Channel Mapping'}
          </h2>
          <form onSubmit={(e) => { e.preventDefault(); saveChannelMapping(mappingFormState); }} className="space-y-6">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Discord Channel</label>
              {manualChannelEntry ? (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      value={mappingFormState.channel_id}
                      onChange={(e) => !selectedMapping && setMappingFormState(prev => ({ ...prev, channel_id: e.target.value.trim() }))}
                      className={`flex-1 min-w-[200px] px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 ${selectedMapping ? 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed' : 'border-gray-300'}`}
                      placeholder="Channel ID (e.g. 123456789012345678)"
                      readOnly={Boolean(selectedMapping)}
                      required
                    />
                    {!selectedMapping && (
                      <button
                        type="button"
                        onClick={() => setManualChannelEntry(false)}
                        className="px-4 py-3 rounded-xl border-2 border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 whitespace-nowrap"
                      >
                        Choose from list
                      </button>
                    )}
                  </div>
                  <p className="text-gray-500 text-xs">
                    {selectedMapping
                      ? 'Channel is locked for this mapping. Delete and recreate to use a different channel.'
                      : 'Enter the Discord channel ID (numeric, 17–19 digits). Right‑click the channel in Discord with Developer Mode on → Copy Channel ID.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={mappingFormState.channel_id}
                      onChange={(e) => setMappingFormState((prev) => applyChannelSelectionToMappingForm(prev, e.target.value, availableChannels))}
                      className={`flex-1 min-w-[200px] px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 ${selectedMapping ? 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed' : 'border-gray-300'}`}
                      disabled={Boolean(selectedMapping)}
                      required
                    >
                      <option value="">Select a channel...</option>
                      {availableChannels.map((channel) => (
                        <option key={channel.channel_id} value={channel.channel_id}>{channel.channel_name}</option>
                      ))}
                    </select>
                    {!selectedMapping && (
                      <button
                        type="button"
                        onClick={() => setManualChannelEntry(true)}
                        className="px-4 py-3 rounded-xl border-2 border-blue-500 bg-white text-blue-600 font-medium hover:bg-blue-50 flex items-center gap-2 whitespace-nowrap"
                        title="Enter Discord channel ID manually"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        Enter channel ID manually
                      </button>
                    )}
                  </div>
                  <p className="text-gray-500 text-xs">
                    {selectedMapping
                      ? 'Channel is locked for this mapping. Delete and recreate to use a different channel.'
                      : 'Select the Discord channel where you receive trade signals, or use the button to enter a channel ID manually.'}
                  </p>
                </div>
              )}
            </div>

            {/* Warning: channel cannot be changed on an existing mapping */}
            {selectedMapping && mappingFormState.channel_id && selectedMapping.channel_id &&
              mappingFormState.channel_id !== String(selectedMapping.channel_id) && (
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex gap-3">
                <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <div>
                  <p className="text-amber-800 font-semibold text-sm">Channel cannot be changed on an existing mapping</p>
                  <p className="text-amber-700 text-sm mt-1">
                    The channel is locked once a mapping is created — it is part of your credential key and SQS queue name.
                    To switch to a different channel, <strong>delete this mapping</strong> and create a new one with the channel you want.
                  </p>
                </div>
              </div>
            )}

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Discord User ID
                <button
                  type="button"
                  className="ml-2 text-blue-500 hover:text-blue-700"
                  onClick={() => {
                    alert(`How to get your Discord User ID:

1. Enable Developer Mode in Discord:
   • Open Discord Settings (⚙️)
   • Go to Advanced → Developer Mode
   • Toggle ON Developer Mode

2. Copy your User ID:
   • Right-click on your username in any server
   • Select "Copy User ID"
   • You'll get a long number like: 792475193137299476

3. Paste it here!

Note: The User ID is different from your username. It's a unique 18-19 digit number.`);
                  }}
                  title="How to get Discord User ID"
                >
                <svg className="w-4 h-4 inline" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" /></svg>
                </button>
              </label>
              <input
                type="text"
                value={mappingFormState.discord_user_id}
                onChange={(e) => setMappingFormState(prev => ({ ...prev, discord_user_id: e.target.value }))}
                className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  mappingValidationState.discordUserId.status === 'valid' ? 'border-green-500' :
                  mappingValidationState.discordUserId.status === 'invalid' || mappingValidationState.discordUserId.status === 'error' ? 'border-red-500' :
                  mappingValidationState.discordUserId.status === 'validating' ? 'border-yellow-500' : 'border-gray-300'
                }`}
                placeholder="17-19 digit Discord User ID"
                required
              />
              <p className="text-gray-500 text-xs mt-1">
                Right-click your username in Discord and select "Copy User ID"
              </p>
              {mappingValidationState.discordUserId.message && (
                <p className={`text-xs mt-1 ${mappingValidationState.discordUserId.status === 'valid' ? 'text-green-600' : mappingValidationState.discordUserId.status === 'invalid' || mappingValidationState.discordUserId.status === 'error' ? 'text-red-600' : 'text-yellow-600'}`}>
                  {mappingValidationState.discordUserId.message}
                </p>
              )}
            </div>

            <div className="border-t pt-6 mt-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Trading Details</h3>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Trading Platform</label>
              <select
                value={mappingFormState.trading_platform}
                onChange={(e) => setMappingFormState(prev => ({ ...prev, trading_platform: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="tradier">Tradier</option>
                <option value="ibkr">IBKR</option>
                <option value="schwab">Schwab</option>
              </select>
              <p className="text-gray-500 text-xs mt-1">Field labels below update based on selected platform</p>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {getPlatformLabels(mappingFormState.trading_platform).token}
                <button type="button" className="ml-2 text-blue-500 hover:text-blue-700" title="API token for the selected platform">
                  <svg className="w-4 h-4 inline" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" /></svg>
                </button>
              </label>
              <div className="relative">
                <input
                  type={showMappingToken ? 'text' : 'password'}
                  value={showMappingToken ? mappingFormState.api_token : (mappingFormState.api_token || '')}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (isMaskedCredential(v) || isBulletPlaceholder(v)) return;
                    if (!showMappingToken && mappingFormState.api_token) setShowMappingToken(true);
                    setMappingFormState(prev => ({ ...prev, api_token: v }));
                  }}
                  className={`w-full px-4 py-3 pr-12 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    mappingValidationState.apiToken.status === 'valid' ? 'border-green-500' :
                    mappingValidationState.apiToken.status === 'invalid' || mappingValidationState.apiToken.status === 'error' ? 'border-red-500' :
                    mappingValidationState.apiToken.status === 'validating' ? 'border-yellow-500' : 'border-gray-300'
                  }`}
                  placeholder={selectedMapping ? 'Enter new token to update' : `Enter your ${getPlatformLabels(mappingFormState.trading_platform).token}`}
                  required={!selectedMapping}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                  {mappingValidationState.apiToken.status === 'valid' && (
                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  )}
                  {(mappingValidationState.apiToken.status === 'invalid' || mappingValidationState.apiToken.status === 'error') && (
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  )}
                  {mappingValidationState.apiToken.status === 'validating' && (
                    <svg className="animate-spin w-5 h-5 text-yellow-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                  )}
                  {(mappingFormState.api_token || hasMappingApiToken) && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (showMappingToken) {
                          setShowMappingToken(false);
                          return;
                        }
                        if (hasMappingApiToken && isMaskedCredential(mappingFormState.api_token)) {
                          await handleRevealMappingToken();
                          return;
                        }
                        setShowMappingToken(true);
                      }}
                      disabled={revealBusy}
                      className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                      title={showMappingToken ? 'Hide token' : hasMappingApiToken ? 'Reveal token (password required)' : 'Show token'}
                    >
                      {showMappingToken ? <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878L3 3m6.878 6.878L21 21" /></svg> : <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3  0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>}
                    </button>
                  )}
                </div>
              </div>
              <p className="text-gray-500 text-xs mt-1">
                Get this from your {mappingFormState.trading_platform === 'tradier' ? 'Tradier' : mappingFormState.trading_platform === 'ibkr' ? 'IBKR' : 'Schwab'} account settings
                {(mappingFormState.api_token || hasMappingApiToken) && (
                  <span className="text-green-600 font-medium"> • Token saved (enter new token to update, or reveal to view)</span>
                )}
              </p>
              {mappingValidationState.apiToken.message && (
                <p className={`text-xs mt-1 ${mappingValidationState.apiToken.status === 'valid' ? 'text-green-600' : mappingValidationState.apiToken.status === 'invalid' || mappingValidationState.apiToken.status === 'error' ? 'text-red-600' : 'text-yellow-600'}`}>
                  {mappingValidationState.apiToken.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {getPlatformLabels(mappingFormState.trading_platform).accountId}
                <button
                  type="button"
                  className="ml-2 text-blue-500 hover:text-blue-700"
                  onClick={() => {
                    if (mappingFormState.trading_platform === 'tradier') {
                      alert(`How to get your Tradier Account ID:

1. Log into your Tradier account at tradier.com
2. Go to Account Settings or Account Information
3. Look for "Account Number" or "Account ID"
4. It's typically a 6-8 digit number
5. Copy and paste it here

Note: This is different from your API key. It's your actual trading account number.`);
                    } else {
                      alert(`Enter your ${mappingFormState.trading_platform === 'ibkr' ? 'IBKR' : 'Schwab'} account number from your brokerage account settings. This is your actual trading account number, not your API key.`);
                    }
                  }}
                  title="How to get Account ID"
                >
                <svg className="w-4 h-4 inline" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" /></svg>
                </button>
              </label>
              <input
                type="text"
                value={mappingFormState.account_id}
                onChange={(e) => setMappingFormState(prev => ({ ...prev, account_id: e.target.value }))}
                className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  mappingValidationState.accountId.status === 'valid' ? 'border-green-500' :
                  mappingValidationState.accountId.status === 'invalid' || mappingValidationState.accountId.status === 'error' ? 'border-red-500' :
                  mappingValidationState.accountId.status === 'validating' ? 'border-yellow-500' : 'border-gray-300'
                }`}
                placeholder={`Enter your ${getPlatformLabels(mappingFormState.trading_platform).accountId}`}
                required
              />
              <p className="text-gray-500 text-xs mt-1">
                This is your actual trading account number, not your API credentials
              </p>
              {mappingValidationState.accountId.message && (
                <p className={`text-xs mt-1 ${mappingValidationState.accountId.status === 'valid' ? 'text-green-600' : mappingValidationState.accountId.status === 'invalid' || mappingValidationState.accountId.status === 'error' ? 'text-red-600' : 'text-yellow-600'}`}>
                  {mappingValidationState.accountId.message}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label className="flex items-center">
                <input type="checkbox" id="use_sandbox" checked={mappingFormState.use_sandbox} onChange={(e) => setMappingFormState(prev => ({ ...prev, use_sandbox: e.target.checked }))} className="mr-2 h-4 w-4 rounded border-gray-300" />
                <span className="text-gray-700 text-sm font-bold">{getPlatformLabels(mappingFormState.trading_platform).sandboxLabel}</span>
              </label>
              <p className="text-gray-500 text-xs mt-1 ml-6">
                {getPlatformLabels(mappingFormState.trading_platform).sandboxHelp}
              </p>
            </div>

            {/* Risk Management Settings - same layout and help text as Trading Config */}
            <div className="border-t pt-6 mt-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Risk Management Settings</h3>

              {/* Trade Size Multiplier — editable when admin enabled multiplier for this user */}
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {RISK_PARAM_LABELS.tradeSizeMultiplier}
                  {!userMultiplierEnabled && (
                    <span className="ml-2 text-xs text-gray-500 font-normal">(Admin Only)</span>
                  )}
                </label>
                <select
                  className={`w-full px-4 py-3 rounded-xl border ${
                    userMultiplierEnabled
                      ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500'
                      : 'border-gray-300 bg-gray-100 text-gray-700 cursor-not-allowed'
                  }`}
                  value={mappingFormState.trade_size_multiplier ?? DEFAULT_TRADE_SIZE_MULTIPLIER}
                  disabled={!userMultiplierEnabled}
                  onChange={(e) =>
                    setMappingFormState((prev) => ({
                      ...prev,
                      trade_size_multiplier: parseTradeSizeMultiplier(e.target.value),
                    }))
                  }
                >
                  {(userMultiplierEnabled
                    ? ALL_TRADE_SIZE_MULTIPLIER_OPTIONS
                    : [DEFAULT_TRADE_SIZE_MULTIPLIER]
                  ).map((option) => (
                    <option key={option} value={option}>
                      {option}X
                    </option>
                  ))}
                </select>
                <p className="text-gray-500 text-xs mt-1">
                  {userMultiplierEnabled
                    ? 'Select 1X through 5X. An administrator must enable this option for your account first.'
                    : 'Fixed at 1X. Contact an administrator to enable higher multipliers.'}
                </p>
              </div>

              {/* Maximum Trades Per Day */}
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">{RISK_PARAM_LABELS.maximumTradesPerDay}</label>
                <input
                  type="number"
                  value={mappingFormState.max_daily_trades}
                  onChange={(e) => setMappingFormState(prev => ({ ...prev, max_daily_trades: parseInt(e.target.value, 10) || DEFAULT_MAX_DAILY_TRADES }))}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  max="100"
                />
                <p className="text-gray-500 text-xs mt-1">
                  Default is {DEFAULT_MAX_DAILY_TRADES} (same as standing instruction)
                </p>
              </div>

              {/* Maximum Dollar Amount Per Trade */}
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">{RISK_PARAM_LABELS.maximumDollarAmountPerTrade} ($)</label>
                <input
                  type="number"
                  value={mappingFormState.max_single_trade_amount}
                  onChange={(e) => setMappingFormState(prev => applySyncedDollarLimitsToForm(prev, e.target.value))}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="100"
                  step="100"
                />
              </div>

              {/* Maximum Daily Dollar Exposure */}
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">{RISK_PARAM_LABELS.maximumDailyDollarExposure} ($)</label>
                <input
                  type="number"
                  value={mappingFormState.max_daily_dollar_exposure}
                  onChange={(e) => setMappingFormState(prev => applySyncedDollarLimitsToForm(prev, e.target.value))}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="100"
                  step="100"
                />
                <p className="text-gray-500 text-xs mt-1">
                  {CHALLENGE_TIER_DOLLAR_LIMIT_HELP}
                </p>
              </div>

              {/* Price Tolerance Threshold (%) */}
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {RISK_PARAM_LABELS.priceToleranceThreshold}
                </label>
                <input
                  type="number"
                  value={mappingFormState.price_deviation_limit}
                  onChange={(e) => setMappingFormState(prev => ({ ...prev, price_deviation_limit: parseFloat(e.target.value) || DEFAULT_PRICE_TOLERANCE_THRESHOLD }))}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  max="100"
                />
                <p className="text-gray-500 text-xs mt-1">
                  Maximum allowed deviation from the signal price before a trade is rejected
                </p>
              </div>

              {/* Eligible Order Types */}
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">{RISK_PARAM_LABELS.eligibleOrderTypes}</label>
                <select
                  value={mappingFormState.eligible_order_types}
                  onChange={(e) => setMappingFormState(prev => ({ ...prev, eligible_order_types: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {ELIGIBLE_ORDER_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-gray-500 text-xs mt-1">
                  Order types you authorize for automated execution (standing instruction)
                </p>
              </div>

              {/* Enable Price Checks - on by default; warn when disabling */}
              <div className="mb-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mappingFormState.enable_price_checks}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        if (!window.confirm('Disabling price checks will allow trades to execute without verifying price tolerance. This will allow your trading to become more risky. Are you sure?')) return;
                      }
                      setMappingFormState(prev => ({ ...prev, enable_price_checks: e.target.checked }));
                    }}
                    className="mr-2 h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-gray-700 text-sm font-bold">{RISK_PARAM_LABELS.enablePriceChecks}</span>
                </label>
                <p className="text-gray-500 text-xs mt-1 ml-6">
                  Verify trade price is within the tolerance threshold before executing (recommended)
                </p>
                {!mappingFormState.enable_price_checks && (
                  <p className="text-amber-600 text-xs mt-1 ml-6 font-medium flex items-center gap-1.5">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    Trades can execute without price verification; this can make your trading riskier.
                  </p>
                )}
              </div>

              {/* Denied tickers — BTO skip list (issue #31) */}
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {RISK_PARAM_LABELS.deniedTickers}
                </label>
                <input
                  type="text"
                  value={mappingFormState.denied_tickers}
                  onChange={(e) =>
                    setMappingFormState((prev) => ({ ...prev, denied_tickers: e.target.value }))
                  }
                  placeholder="TSLA, AAPL, NVDA"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-gray-500 text-xs mt-1">
                  Comma-separated underlying symbols (max {MAX_DENIED_TICKERS}). BTO signals for
                  these tickers are skipped; STC is still allowed.
                </p>
              </div>

              {/* Enable Deviation Override Prompt — off by default (issue #13) */}
              <div className="mb-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mappingFormState.enable_deviation_override}
                    onChange={(e) => {
                      setMappingFormState(prev => ({
                        ...prev,
                        enable_deviation_override: e.target.checked,
                      }));
                    }}
                    className="mr-2 h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-gray-700 text-sm font-bold">
                    {RISK_PARAM_LABELS.enableDeviationOverride}
                  </span>
                </label>
                <p className="text-gray-500 text-xs mt-1 ml-6">
                  After all price-deviation retries fail, send an interactive Discord prompt to
                  execute at market or dismiss (deviation check only; other guards still apply)
                </p>
              </div>

              {/* Auto Approve */}
              <div className="mb-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mappingFormState.auto_approve_enabled}
                    onChange={(e) => {
                      const nextOn = e.target.checked;
                      if (nextOn && !hasSmsPhoneOnFile) {
                        setError(
                          'Add a mobile phone in User Settings (Risk Management → Phone number for SMS alerts) before enabling auto-approve.'
                        );
                        return;
                      }
                      setError('');
                      setMappingFormState(prev => ({ ...prev, auto_approve_enabled: nextOn }));
                    }}
                    className="mr-2 h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-gray-700 text-sm font-bold">{RISK_PARAM_LABELS.autoApproveTrades}</span>
                </label>
                <p className="text-gray-500 text-xs mt-1 ml-6">
                  Automatically approve and execute trade signals without manual confirmation. Each quarter,
                  use the <span className="font-medium">Reconfirm</span> button on your channel card when it
                  appears — you do not need to turn this off and on.
                </p>
                <p className="text-gray-500 text-xs mt-1 ml-6">
                  <span className="font-medium">Phone required:</span> enabling auto-approve needs a mobile number on file
                  for quarterly email and SMS reminders.{' '}
                  {!hasSmsPhoneOnFile ? (
                    <button
                      type="button"
                      className="text-blue-600 underline font-medium"
                      onClick={() => {
                        setShowMappingForm(false);
                        setCurrentView('settings');
                        setMessage('Add your SMS phone number below, save settings, then return to Channel Config.');
                      }}
                    >
                      Add phone in User Settings
                    </button>
                  ) : (
                    <span className="text-green-700">Phone on file.</span>
                  )}
                </p>
              </div>
            </div>

            {error && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3"><p className="text-yellow-800 text-sm font-medium">{error}</p></div>}
            {message && <div className="bg-green-50 border border-green-200 rounded-lg p-3"><p className="text-green-800 text-sm font-medium">{message}</p></div>}
            {(mappingValidationState.apiToken.status === 'invalid' || mappingValidationState.apiToken.status === 'error' || mappingValidationState.accountId.status === 'invalid' || mappingValidationState.accountId.status === 'error' || mappingValidationState.discordUserId.status === 'invalid' || mappingValidationState.discordUserId.status === 'error') && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-700 text-sm font-medium mb-2">Validation errors:</p>
                <ul className="text-red-600 text-xs space-y-1">
                  {(mappingValidationState.discordUserId.status === 'invalid' || mappingValidationState.discordUserId.status === 'error') && <li>{mappingValidationState.discordUserId.message}</li>}
                  {(mappingValidationState.apiToken.status === 'invalid' || mappingValidationState.apiToken.status === 'error') && <li>{mappingValidationState.apiToken.message}</li>}
                  {(mappingValidationState.accountId.status === 'invalid' || mappingValidationState.accountId.status === 'error') && <li>{mappingValidationState.accountId.message}</li>}
                </ul>
              </div>
            )}
            {mappingValidated && (mappingValidationState.discordUserId.status === 'valid' && (mappingFormState.trading_platform !== 'tradier' || (mappingValidationState.apiToken.status === 'valid' && mappingValidationState.accountId.status === 'valid'))) && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3"><p className="text-green-700 text-sm font-medium">✅ All fields validated. Ready to save.</p></div>
            )}

            <div className="flex gap-4">
              <button type="button" onClick={handleValidateMapping} disabled={validatingMapping || loading} className="flex-1 bg-green-600 text-white font-bold py-3 px-4 rounded-xl hover:bg-green-700 transition duration-200 shadow-lg disabled:opacity-50 flex items-center justify-center">
                {validatingMapping ? (<><svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Validating...</>) : (<><svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>Validate</>)}
              </button>
              <button type="submit" disabled={loading || !mappingValidated || (selectedMapping && mappingFormState.channel_id && selectedMapping.channel_id && mappingFormState.channel_id !== String(selectedMapping.channel_id))} className="flex-1 bg-blue-600 text-white font-bold py-3 px-4 rounded-xl hover:bg-blue-700 transition duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center" title={selectedMapping && mappingFormState.channel_id !== String(selectedMapping.channel_id) ? 'Delete this mapping and create a new one to change the channel' : !mappingValidated ? 'Validate first' : 'Save mapping'}>
                {loading ? (<><svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Saving...</>) : (<><svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Save</>)}
              </button>
              <button
                type="button"
                onClick={() => { setMessage(''); setError(''); setSelectedMapping(null); setShowMappingForm(false); setMappingFormState(createDefaultMappingFormState()); setMappingValidationState({ apiToken: { status: 'pending', message: '' }, accountId: { status: 'pending', message: '' }, discordUserId: { status: 'pending', message: '' } }); setMappingValidated(false); setManualChannelEntry(false); }}
                className="bg-gray-200 text-gray-700 font-bold py-3 px-4 rounded-xl hover:bg-gray-300 transition duration-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
        )}

        {/* Channel Mappings Grid */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading channel mappings...</p>
          </div>
        ) : channelMappings.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Channel Mappings Yet</h3>
            <p className="text-gray-500 mb-4">Create your first channel-account mapping to start trading</p>
            <button
              onClick={() => {
                setSelectedMapping(null);
                setMessage('');
                setError('');
                setShowMappingForm(true);
                setMappingFormState(createDefaultMappingFormState());
                setMappingValidationState({
                  apiToken: { status: 'pending', message: '' },
                  accountId: { status: 'pending', message: '' },
                  discordUserId: { status: 'pending', message: '' }
                });
                setMappingValidated(false);
                loadAvailableChannels();
                loadAvailablePlatforms();
              }}
              className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition duration-200"
            >
              Create First Mapping
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {channelMappings.map((mapping) => (
              <div key={mapping.id} className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-800 text-lg">{mapping.channel_name || `Channel ${mapping.channel_id}`}</h3>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${
                      mapping.trading_platform === 'tradier' ? 'bg-blue-100 text-blue-800' :
                      mapping.trading_platform === 'ibkr' ? 'bg-purple-100 text-purple-800' :
                      mapping.trading_platform === 'schwab' ? 'bg-indigo-100 text-indigo-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {(mapping.trading_platform || 'tradier').toUpperCase()}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedMapping(mapping);
                        setMessage('');
                        setError('');
                        setShowMappingForm(true);
                        setShowMappingToken(false);
                        setHasMappingApiToken(Boolean(mapping.has_api_token ?? mapping.api_token_masked ?? mapping.api_token));
                        setMappingFormState({
                          channel_id: mapping.channel_id,
                          trading_platform: mapping.trading_platform || 'tradier',
                          account_id: mapping.account_id || '',
                          api_token: mapping.api_token_masked || mapping.api_token || '',
                          discord_user_id: mapping.discord_user_id || '',
                          use_sandbox: mapping.use_sandbox || false,
                          trade_size_multiplier: tradeSizeMultiplierFromMapping(
                            mapping,
                            userMultiplierEnabled
                          ),
                          max_daily_trades: mapping.max_daily_trades ?? DEFAULT_MAX_DAILY_TRADES,
                          max_single_trade_amount: mapping.max_single_trade_amount ?? DEFAULT_MAX_SINGLE_TRADE_AMOUNT,
                          max_daily_dollar_exposure: resolveMaxDailyDollarExposure(mapping),
                          price_deviation_limit: mapping.price_deviation_limit ?? DEFAULT_PRICE_TOLERANCE_THRESHOLD,
                          eligible_order_types: normalizeEligibleOrderTypes(mapping.eligible_order_types),
                          enable_price_checks: mapping.enable_price_checks ?? true,
                          denied_tickers: formatDeniedTickersForDisplay(mapping.denied_tickers),
                          enable_deviation_override: mapping.enable_deviation_override ?? false,
                          auto_approve_enabled: mapping.auto_approve_enabled ?? false
                        });
                        setMappingValidationState({
                          apiToken: { status: 'pending', message: '' },
                          accountId: { status: 'pending', message: '' },
                          discordUserId: { status: 'pending', message: '' }
                        });
                        setMappingValidated(false);
                        setManualChannelEntry(false);
                        loadAvailableChannels();
                        loadAvailablePlatforms();
                      }}
                      className="text-blue-600 hover:text-blue-800 p-1"
                      title="Edit"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => deleteChannelMapping(mapping.id)}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Delete"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Account ID:</span>
                    <span className="text-gray-800 font-mono">{mapping.account_id || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Environment:</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${mapping.use_sandbox ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                      {mapping.use_sandbox ? 'Sandbox' : 'Production'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Multiplier:</span>
                    <span className="text-gray-800">
                      {tradeSizeMultiplierFromMapping(mapping, userMultiplierEnabled)}x
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Auto-approve:</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${mapping.auto_approve_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {mapping.auto_approve_enabled ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2 items-start">
                    <span className="text-gray-500 shrink-0">Denied tickers:</span>
                    <span
                      className={`text-right font-medium ${
                        hasDeniedTickers(mapping) ? 'text-amber-800' : 'text-gray-600'
                      }`}
                      title="BTO signals for these symbols are skipped; STC is still allowed"
                    >
                      {deniedTickersSummaryForMapping(mapping)}
                    </span>
                  </div>
                </div>
                <AutoApproveReconfirmBlock
                  mapping={mapping}
                  apiReconfirmConfig={autoApproveReconfirmFromApi}
                  onOpenReconfirmModal={(mm) => setAutoApproveReconfirmModalMapping(mm)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderTradingAnalytics = () => (
    <div className="max-w-7xl mx-auto p-8">
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-gray-800">Trading Analytics</h2>
          <div className="flex space-x-2">
            {['7d', '30d', '90d', '1y'].map((period) => (
              <button
                key={period}
                onClick={() => setAnalyticsPeriod(period)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition duration-200 ${
                  analyticsPeriod === period
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {period}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading analytics...</p>
          </div>
        ) : tradingAnalytics.error ? (
          <div className="text-center py-8">
            <p className="text-red-500">Error loading analytics: {tradingAnalytics.error}</p>
          </div>
        ) : (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">Total Trades</h3>
                <p className="text-3xl font-bold text-blue-600">{tradingAnalytics.total_trades || 0}</p>
              </div>
              <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                <h3 className="text-lg font-semibold text-green-800 mb-2">Win Rate</h3>
                <p className="text-3xl font-bold text-green-600">{tradingAnalytics.win_rate || 0}%</p>
              </div>
              <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-800 mb-2">Total P&L</h3>
                <p className={`text-3xl font-bold ${(tradingAnalytics.total_pnl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(tradingAnalytics.total_pnl)}
                </p>
              </div>
              <div className="bg-orange-50 p-6 rounded-xl border border-orange-200">
                <h3 className="text-lg font-semibold text-orange-800 mb-2">Avg Trade Size</h3>
                <p className="text-3xl font-bold text-orange-600">{formatCurrency(tradingAnalytics.avg_trade_size)}</p>
              </div>
            </div>

            {/* Top Performing Symbols */}
            {tradingAnalytics.top_symbols && tradingAnalytics.top_symbols.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Top Performing Symbols</h3>
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {tradingAnalytics.top_symbols.map((symbol, index) => (
                      <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-gray-800">{symbol.symbol}</span>
                          <span className={`text-sm font-medium ${symbol.symbol_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(symbol.symbol_pnl)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{symbol.trade_count} trades</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Monthly Performance Chart */}
            {tradingAnalytics.monthly_performance && tradingAnalytics.monthly_performance.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Monthly Performance</h3>
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="space-y-4">
                    {tradingAnalytics.monthly_performance.map((month, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center space-x-4">
                          <span className="font-medium text-gray-800">
                            {new Date(month.month).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                          </span>
                          <span className="text-sm text-gray-600">{month.monthly_trades} trades</span>
                        </div>
                        <span className={`font-semibold ${month.monthly_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(month.monthly_pnl)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  const renderSettingsForm = () => (
    <div className="bg-white p-4 md:p-8 rounded-2xl shadow-xl w-full max-w-2xl mx-auto my-4 md:my-8">
      <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-6">Account Settings</h2>
      <form onSubmit={handleSaveSettings} className="space-y-6">

        <div className="border-b pb-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Notifications</h3>

          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                name="setting_discordNotifications"
                checked={userSettings.discordNotifications}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Discord notifications
              </label>
            </div>
          </div>
        </div>

        <div className="border-b pb-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Risk Management Settings</h3>
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="setting_smsCriticalAlerts"
                  checked={userSettings.smsCriticalAlerts}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 block text-sm font-medium text-gray-900">
                  Critical SMS alerts for failed trades
                </span>
              </label>
              <p className="text-gray-500 text-xs ml-6">
                Send an SMS when an automated trade fails so you can execute it manually. Supports US (+1) and international (e.g. India +91) numbers. Standard carrier rates may apply.
              </p>
              {!userSettings.smsCriticalAlerts && userSettings.smsPhoneNumber && (
                <p className="text-amber-600 text-xs ml-6">
                  SMS alerts are currently off. Check the box above and save to re-enable them.
                </p>
              )}
            </div>
            <div className="ml-6">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                Mobile phone number <span className="text-red-600">*</span> (SMS &amp; auto-approve)
              </label>
              <input
                type="tel"
                name="setting_smsPhoneNumber"
                value={userSettings.smsPhoneNumber || ''}
                onChange={handleInputChange}
                placeholder="e.g. +12025551234 or +919876543210"
                className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                maxLength="20"
              />
              <p className="text-gray-500 text-xs mt-1">
                Use E.164 format (+1 US, +91 India, etc.). Required to enable auto-approve on a channel (quarterly email
                and SMS reminders). Also used for critical trade-failure SMS when that option is enabled above.
              </p>
            </div>

            {/* SMS consent opt-in (optional) */}
            <div className="ml-0 mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-800">
                    SMS Alerts consent{' '}
                    {hasAcceptedSms ? (
                      <span className="inline-block rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                        Opted in
                      </span>
                    ) : (
                      <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                        Optional — not yet signed
                      </span>
                    )}
                  </p>
                  <p className="mt-1 text-xs text-blue-700">
                    {hasAcceptedSms
                      ? 'You have signed the SMS consent. SMS alerts are enabled for your account.'
                      : 'You skipped SMS consent during onboarding. Opt in now to enable SMS alerts for failed trades and auto-approve reminders.'}
                  </p>
                </div>
                {!hasAcceptedSms && (
                  <button
                    type="button"
                    onClick={() => setCurrentView('agreements_sms')}
                    className="shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Opt in to SMS
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="border-b pb-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Account Settings</h3>

          {/* Password Update */}
          <div className="mb-6">
            <h4 className="text-md font-medium text-gray-600 mb-3">Update Password</h4>
            <form onSubmit={handleUpdatePassword} className="space-y-3">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Current Password</label>
                <input
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  type="password"
                  name="account_currentPassword"
                  value={accountSettings.currentPassword}
                  onChange={handleInputChange}
                  placeholder="Enter current password"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">New Password</label>
                <input
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  type="password"
                  name="account_newPassword"
                  value={accountSettings.newPassword}
                  onChange={handleInputChange}
                  placeholder="Enter new password (min 8 characters)"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Confirm New Password</label>
                <input
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  type="password"
                  name="account_confirmNewPassword"
                  value={accountSettings.confirmNewPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm new password"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 text-white font-bold py-2 px-4 rounded-xl hover:bg-green-700 transition duration-200 shadow-lg disabled:opacity-50 flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </>
                ) : 'Update Password'}
              </button>
            </form>
          </div>

          {/* Phone Number Update */}
          <div>
            <h4 className="text-md font-medium text-gray-600 mb-3">Update Phone Number</h4>
            <form onSubmit={handleUpdatePhoneNumber} className="space-y-3">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Current Phone Number</label>
                <input
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-gray-100"
                  type="text"
                  value={userData?.phoneNumber || 'Not set'}
                  disabled
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">New Phone Number</label>
                <input
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  type="tel"
                  name="account_newPhoneNumber"
                  value={accountSettings.newPhoneNumber}
                  onChange={handleInputChange}
                  placeholder="Enter new phone number (numbers only)"
                  pattern="[0-9]*"
                  maxLength="15"
                />
                <p className="text-gray-500 text-xs mt-1">
                  Enter numbers only. Examples: "9876543210" (US) or "529876543210" (international)
                </p>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-purple-600 text-white font-bold py-2 px-4 rounded-xl hover:bg-purple-700 transition duration-200 shadow-lg disabled:opacity-50 flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </>
                ) : 'Update Phone Number'}
              </button>
            </form>
          </div>
        </div>

        {error && <p className="text-red-500 text-center text-sm">{error}</p>}
        {message && <p className="text-green-500 text-center text-sm">{message}</p>}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-xl hover:bg-blue-700 transition duration-200 shadow-lg disabled:opacity-50 flex items-center justify-center"
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </>
          ) : 'Save Settings'}
        </button>
      </form>
    </div>
  );

  const renderTokenForm = () => (
    <div className="bg-white p-4 md:p-8 rounded-2xl shadow-xl w-full max-w-2xl mx-auto my-4 md:my-8 relative">
      <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-6">Trading Configuration</h2>

      {/* Loading overlay for configuration loading */}
      {loadingConfig && (
        <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-2xl z-10">
          <div className="text-center">
            <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600 font-medium">Loading your configuration...</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSaveConfiguration} className="space-y-6">

        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2">Tradier API Token</label>
          <div className="relative">
          <input
              className={`w-full px-4 py-3 pr-12 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                validationState.tradierToken.status === 'valid' ? 'border-green-500' :
                validationState.tradierToken.status === 'invalid' ? 'border-red-500' :
                validationState.tradierToken.status === 'validating' ? 'border-yellow-500' :
                'border-gray-300'
              }`}
              type={showToken ? "text" : "password"}
            name="tradierToken"
              value={showToken ? formState.tradierToken : (formState.tradierToken || '')}
            onChange={handleInputChange}
              placeholder={formState.tradierToken ? "Token saved - enter new token to update" : "Enter your Tradier API token"}
            required
          />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
              {validationState.tradierToken.status === 'valid' && (
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {validationState.tradierToken.status === 'invalid' && (
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              {validationState.tradierToken.status === 'validating' && (
                <svg className="animate-spin w-5 h-5 text-yellow-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {(formState.tradierToken || hasTradierToken) && (
                <button
                  type="button"
                  onClick={async () => {
                    if (showToken) {
                      setShowToken(false);
                      return;
                    }
                    if (hasTradierToken && isMaskedCredential(formState.tradierToken)) {
                      await handleRevealConfigurationToken();
                      return;
                    }
                    setShowToken(true);
                  }}
                  disabled={revealBusy}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none disabled:opacity-50"
                  title={showToken ? 'Hide token' : hasTradierToken ? 'Reveal token (password required)' : 'Show token'}
                >
                  {showToken ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>
          <p className="text-gray-500 text-xs mt-1">
            Get this from your Tradier account settings
            {(formState.tradierToken || hasTradierToken) && (
              <span className="text-green-600 font-medium"> • Token saved (enter new token to update, or reveal to view)</span>
            )}
          </p>
          {validationState.tradierToken.message && (
            <p className={`text-xs mt-1 ${
              validationState.tradierToken.status === 'valid' ? 'text-green-600' :
              validationState.tradierToken.status === 'invalid' ? 'text-red-600' :
              validationState.tradierToken.status === 'validating' ? 'text-yellow-600' :
              'text-gray-600'
            }`}>
              {validationState.tradierToken.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Tradier Account ID
            <button
              type="button"
              className="ml-2 text-blue-500 hover:text-blue-700"
              onClick={() => {
                alert(`How to get your Tradier Account ID:

1. Log into your Tradier account at tradier.com
2. Go to Account Settings or Account Information
3. Look for "Account Number" or "Account ID"
4. It's typically a 6-8 digit number
5. Copy and paste it here

Note: This is different from your API key. It's your actual trading account number.`);
              }}
              title="How to get Tradier Account ID"
            >
              <svg className="w-4 h-4 inline" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
            </button>
          </label>
          <div className="relative">
          <input
              className={`w-full px-4 py-3 pr-12 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                validationState.tradierAccountId.status === 'valid' ? 'border-green-500' :
                validationState.tradierAccountId.status === 'invalid' ? 'border-red-500' :
                validationState.tradierAccountId.status === 'validating' ? 'border-yellow-500' :
                'border-gray-300'
              }`}
            type="text"
            name="tradierAccountId"
            value={formState.tradierAccountId}
            onChange={handleInputChange}
            placeholder="Enter your Tradier Account ID"
            required
          />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
              {validationState.tradierAccountId.status === 'valid' && (
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {validationState.tradierAccountId.status === 'invalid' && (
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              {validationState.tradierAccountId.status === 'validating' && (
                <svg className="animate-spin w-5 h-5 text-yellow-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
            </div>
          </div>
          <p className="text-gray-500 text-xs mt-1">
            This is your actual trading account number, not your API credentials
          </p>
          {validationState.tradierAccountId.message && (
            <p className={`text-xs mt-1 ${
              validationState.tradierAccountId.status === 'valid' ? 'text-green-600' :
              validationState.tradierAccountId.status === 'invalid' ? 'text-red-600' :
              validationState.tradierAccountId.status === 'validating' ? 'text-yellow-600' :
              'text-gray-600'
            }`}>
              {validationState.tradierAccountId.message}
            </p>
          )}
        </div>

        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="tradierUseSandbox"
              checked={formState.tradierUseSandbox}
              onChange={handleInputChange}
              className="mr-2 h-4 w-4 rounded border-gray-300"
            />
            <span className="text-gray-700 text-sm font-bold">Use Sandbox API</span>
          </label>
          <p className="text-gray-500 text-xs mt-1 ml-6">
            Check this if your Tradier token is for the sandbox environment (https://sandbox.tradier.com/v1/)
          </p>
        </div>

        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Discord User ID
            <button
              type="button"
              className="ml-2 text-blue-500 hover:text-blue-700"
              onClick={() => {
                alert(`How to get your Discord User ID:

1. Enable Developer Mode in Discord:
   • Open Discord Settings (⚙️)
   • Go to Advanced → Developer Mode
   • Toggle ON Developer Mode

2. Copy your User ID:
   • Right-click on your username in any server
   • Select "Copy User ID"
   • You'll get a long number like: 792475193137299476

3. Paste it here!

Note: The User ID is different from your username. It's a unique 18-19 digit number.`);
              }}
              title="How to get Discord User ID"
            >
              <svg className="w-4 h-4 inline" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
            </button>
          </label>
          <div className="relative">
          <input
              className={`w-full px-4 py-3 pr-12 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                validationState.discordUserId.status === 'valid' ? 'border-green-500' :
                validationState.discordUserId.status === 'invalid' ? 'border-red-500' :
                validationState.discordUserId.status === 'validating' ? 'border-yellow-500' :
                'border-gray-300'
              }`}
            type="text"
            name="discordUserId"
            value={formState.discordUserId}
            onChange={handleInputChange}
            placeholder="Enter your Discord user ID (e.g., 123456789012345678)"
            required
          />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
              {validationState.discordUserId.status === 'valid' && (
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {validationState.discordUserId.status === 'invalid' && (
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              {validationState.discordUserId.status === 'validating' && (
                <svg className="animate-spin w-5 h-5 text-yellow-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
            </div>
          </div>
          <p className="text-gray-500 text-xs mt-1">
            Right-click your username in Discord and select "Copy User ID"
          </p>
          {validationState.discordUserId.message && (
            <p className={`text-xs mt-1 ${
              validationState.discordUserId.status === 'valid' ? 'text-green-600' :
              validationState.discordUserId.status === 'invalid' ? 'text-red-600' :
              validationState.discordUserId.status === 'validating' ? 'text-yellow-600' :
              'text-gray-600'
            }`}>
              {validationState.discordUserId.message}
            </p>
          )}
        </div>

        {/* Risk Management Settings */}
        <div className="border-t pt-6 mt-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Risk Management Settings</h3>

          {/* Trade Size Multiplier - Disabled for users (#157: admin enables flag; dropdown stays 1X) */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Trade Size Multiplier
              <span className="ml-2 text-xs text-gray-500 font-normal">(Admin Only)</span>
            </label>
            <select
              className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-gray-100 text-gray-700 cursor-not-allowed"
              value={DEFAULT_TRADE_SIZE_MULTIPLIER}
              disabled
            >
              <option value={DEFAULT_TRADE_SIZE_MULTIPLIER}>{DEFAULT_TRADE_SIZE_MULTIPLIER}X</option>
            </select>
            <p className="text-gray-500 text-xs mt-1">
              Fixed at 1X in the user UI. An admin can enable multiplier for your account,
              but users cannot select 2X/3X here (#157).
            </p>
          </div>

          {/* Maximum Trades Per Day */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">{RISK_PARAM_LABELS.maximumTradesPerDay}</label>
            <input
              type="number"
              name="maxDailyTrades"
              value={formState.maxDailyTrades}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="1"
              max="100"
            />
            <p className="text-gray-500 text-xs mt-1">
              Default is {DEFAULT_MAX_DAILY_TRADES} (same as standing instruction)
            </p>
          </div>

          {/* Maximum Dollar Amount Per Trade */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">{RISK_PARAM_LABELS.maximumDollarAmountPerTrade} ($)</label>
            <input
              type="number"
              name="maxSingleTradeAmount"
              value={formState.maxSingleTradeAmount}
              onChange={(e) => {
                const value = syncMaxDollarLimitFields(e.target.value);
                setFormState(prev => ({ ...prev, maxSingleTradeAmount: value, maxDailyDollarExposure: value }));
              }}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="100"
              step="100"
            />
          </div>

          {/* Maximum Daily Dollar Exposure */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">{RISK_PARAM_LABELS.maximumDailyDollarExposure} ($)</label>
            <input
              type="number"
              name="maxDailyDollarExposure"
              value={formState.maxDailyDollarExposure}
              onChange={(e) => {
                const value = syncMaxDollarLimitFields(e.target.value);
                setFormState(prev => ({ ...prev, maxSingleTradeAmount: value, maxDailyDollarExposure: value }));
              }}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="100"
              step="100"
            />
            <p className="text-gray-500 text-xs mt-1">
              Same value as Maximum Dollar Amount Per Trade. Channel tier auto-fill applies in Channel Configuration.
            </p>
          </div>

          {/* Price Tolerance Threshold (%) */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {RISK_PARAM_LABELS.priceToleranceThreshold}
            </label>
            <input
              type="number"
              name="priceDeviationLimit"
              value={formState.priceDeviationLimit}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="0"
              max="100"
            />
            <p className="text-gray-500 text-xs mt-1">
              Maximum allowed deviation from the signal price before a trade is rejected
            </p>
          </div>

          {/* Enable Price Checks - on by default; warn when disabling */}
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="enablePriceChecks"
                checked={formState.enablePriceChecks}
                onChange={(e) => {
                  if (!e.target.checked) {
                    if (!window.confirm('Disabling price checks will allow trades to execute without verifying price tolerance. This will allow your trading to become more risky. Are you sure?')) return;
                  }
                  setFormState(prev => ({ ...prev, enablePriceChecks: e.target.checked }));
                }}
                className="mr-2 h-4 w-4 rounded border-gray-300"
              />
              <span className="text-gray-700 text-sm font-bold">{RISK_PARAM_LABELS.enablePriceChecks}</span>
            </label>
            <p className="text-gray-500 text-xs mt-1 ml-6">
              Verify trade price is within the tolerance threshold before executing (recommended)
            </p>
            {!formState.enablePriceChecks && (
              <p className="text-amber-600 text-xs mt-1 ml-6 font-medium flex items-center gap-1.5">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                Trades can execute without price verification; this can make your trading riskier.
              </p>
            )}
          </div>

          {/* Auto Approve */}
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="autoApproveEnabled"
                checked={formState.autoApproveEnabled}
                onChange={handleInputChange}
                className="mr-2 h-4 w-4 rounded border-gray-300"
              />
              <span className="text-gray-700 text-sm font-bold">{RISK_PARAM_LABELS.autoApproveTrades}</span>
            </label>
            <p className="text-gray-500 text-xs mt-1 ml-6">
              Automatically approve and execute trade signals without manual confirmation
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p className="text-yellow-800 text-center text-sm font-medium">{error}</p>
          </div>
        )}
        {message && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <p className="text-green-800 text-center text-sm font-medium">{message}</p>
          </div>
        )}

        {/* Validation Summary */}
        {(Object.values(validationState).some(v => v.status === 'invalid') ||
          Object.values(validationState).some(v => v.status === 'error')) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-700 text-sm font-medium mb-2">Validation Errors:</p>
            <ul className="text-red-600 text-xs space-y-1">
              {(validationState.discordUserId.status === 'invalid' || validationState.discordUserId.status === 'error') && (
                <>
                  <li className="font-semibold">❌ Discord Validation Failed:</li>
                  <li className="ml-4 mb-2">{validationState.discordUserId.message}</li>
                </>
              )}
              {(validationState.tradierAccountId.status === 'invalid' || validationState.tradierAccountId.status === 'error') && (
                <>
                  <li className="font-semibold">❌ Tradier Validation Failed:</li>
                  <li className="ml-4 mb-2">{validationState.tradierAccountId.message}</li>
                </>
              )}
              {(validationState.tradierToken.status === 'invalid' || validationState.tradierToken.status === 'error') && (
                <>
                  <li className="font-semibold">❌ Tradier Token Validation Failed:</li>
                  <li className="ml-4 mb-2">{validationState.tradierToken.message}</li>
                </>
              )}
            </ul>
          </div>
        )}

        {/* Validation Success Summary */}
        {Object.values(validationState).every(v => v.status === 'valid') && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <p className="text-green-700 text-sm font-medium">✅ All fields validated successfully! Ready to save.</p>
          </div>
        )}

        <div className="flex gap-4">
          <button
            type="button"
            onClick={handleValidate}
            disabled={validating || loading}
            className="flex-1 bg-green-600 text-white font-bold py-3 px-4 rounded-xl hover:bg-green-700 transition duration-200 shadow-lg disabled:opacity-50 flex items-center justify-center"
          >
            {validating ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Validating...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Validate
              </>
            )}
          </button>
          <button
            type="submit"
            onClick={handleSaveConfiguration}
            disabled={loading || !isValidated}
            className="flex-1 bg-blue-600 text-white font-bold py-3 px-4 rounded-xl hover:bg-blue-700 transition duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            title={!isValidated ? "Please validate your credentials first" : "Save configuration"}
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Save
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  const adminLogGroupButtonLabel = () => {
    const n = adminLogGroupOptions.length;
    const s = adminLogGroupsSelected.length;
    if (n === 0) return 'Loading log groups…';
    if (s === 0) return 'Search and select log groups';
    if (s === n) return `All log groups (${n})`;
    return `${s} of ${n} log groups selected`;
  };

  const filteredAdminLogGroupOptions = adminLogGroupOptions.filter((g) => {
    const q = adminLogGroupSearch.trim().toLowerCase();
    if (!q) return true;
    return g.toLowerCase().includes(q);
  });

  const renderAdminDashboard = () => (
    <div className="max-w-7xl mx-auto p-8">
      <div className="bg-white rounded-2xl shadow-xl p-8">
        {adminSubView === 'users' && (
          <>
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Manage users</h2>
            <div className="flex justify-end mb-4">
              <button
                type="button"
                onClick={() => loadAdminUsers(adminUserSearchQuery)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200 text-sm"
              >
                Refresh Users
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1">
                <h3 className="text-xl font-semibold text-gray-700 mb-3">Users</h3>
                <div className="flex flex-col sm:flex-row gap-2 mb-3">
                  <input
                    type="search"
                    value={adminUserSearchQuery}
                    onChange={(e) => setAdminUserSearchQuery(e.target.value)}
                    placeholder="Search email, Discord ID…"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setAdminUserSearchQuery('')}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Clear
                  </button>
                </div>
                {adminUserSearchError && (
                  <p className="text-xs text-red-600 mb-2">{adminUserSearchError}</p>
                )}
                <p className="text-xs text-gray-500 mb-2">
                  Showing {adminUsers.length === 0 ? 0 : (adminUserPage - 1) * ADMIN_USERS_PAGE_SIZE + 1}
                  –
                  {Math.min(adminUserPage * ADMIN_USERS_PAGE_SIZE, adminUsers.length)} of{' '}
                  {adminUsers.length} (page {adminUserPage} / {adminUserTotalPages})
                </p>
                <div className="space-y-3 max-h-[28rem] overflow-y-auto">
                  {pagedAdminUsers.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => handleUserSelect(user)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedUser?.id === user.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <p className="font-medium text-gray-800 truncate">{user.email}</p>
                          <p className="text-xs text-gray-500 truncate">
                            Discord: {user.discordUserId || '—'}
                          </p>
                          <p className="text-sm text-gray-500">
                            Created:{' '}
                            {user.createdAt
                              ? new Date(user.createdAt).toLocaleDateString()
                              : '—'}
                          </p>
                          <p className="text-sm text-gray-500">
                            Mappings:{' '}
                            <span className={user.hasChannelMappings ? 'text-green-600' : 'text-red-600'}>
                              {user.hasChannelMappings ? '✅' : '❌'}
                            </span>
                          </p>
                          <p className="text-sm text-gray-500">
                            Multiplier:{' '}
                            <span className={user.multiplierEnabled ? 'text-green-600' : 'text-gray-500'}>
                              {user.multiplierEnabled ? 'On' : 'Off'}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between gap-2 mt-3">
                  <button
                    type="button"
                    disabled={adminUserPage <= 1}
                    onClick={() => setAdminUserPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1 text-sm border rounded-lg disabled:opacity-40"
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    disabled={adminUserPage >= adminUserTotalPages}
                    onClick={() => setAdminUserPage((p) => Math.min(adminUserTotalPages, p + 1))}
                    className="px-3 py-1 text-sm border rounded-lg disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>

              <div className="lg:col-span-2">
                {selectedUser ? (
                  <div className="space-y-6">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h3 className="text-xl font-semibold text-gray-700">
                        User Details: {selectedUser.email}
                      </h3>
                      <div className="flex flex-wrap gap-2 items-center">
                        {selectedUser.status === 'inactive' ? (
                          <button
                            type="button"
                            disabled={userLifecycleLoading}
                            onClick={() => handleUserStatusChange(selectedUser.id, 'active')}
                            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition"
                          >
                            Enable
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled={userLifecycleLoading}
                            onClick={() => handleUserStatusChange(selectedUser.id, 'inactive')}
                            className="px-3 py-1.5 text-sm bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 transition"
                          >
                            Disable
                          </button>
                        )}
                        {deleteConfirmUserId === selectedUser.id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-red-700 font-medium">Are you sure?</span>
                            <button
                              type="button"
                              disabled={userLifecycleLoading}
                              onClick={() => handleUserDelete(selectedUser.id)}
                              className="px-3 py-1.5 text-sm bg-red-700 text-white rounded-lg hover:bg-red-800 disabled:opacity-50 transition"
                            >
                              Yes, delete
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteConfirmUserId(null)}
                              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            disabled={userLifecycleLoading}
                            onClick={() => setDeleteConfirmUserId(selectedUser.id)}
                            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                    {userLifecycleError && (
                      <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                        {userLifecycleError}
                      </p>
                    )}

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">Status</p>
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            selectedUser.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {selectedUser.status || 'active'}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Created</p>
                          <p className="font-medium">
                            {selectedUser.createdAt
                              ? new Date(selectedUser.createdAt).toLocaleDateString()
                              : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Discord user ID</p>
                          <p className="font-medium break-all">{selectedUser.discordUserId || '—'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Trade multiplier</p>
                          <p className="font-medium">
                            {selectedUser.multiplierEnabled ? 'Enabled' : 'Disabled'}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {selectedUser.multiplierEnabled ? (
                              <button
                                type="button"
                                disabled={userLifecycleLoading}
                                onClick={() => handleMultiplierToggle(selectedUser.id, false)}
                                className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition"
                              >
                                Disable multiplier
                              </button>
                            ) : (
                              <button
                                type="button"
                                disabled={userLifecycleLoading}
                                onClick={() => handleMultiplierToggle(selectedUser.id, true)}
                                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                              >
                                Enable multiplier
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold text-gray-700 mb-2">Channel mappings</h4>
                      {loadingUserMappings ? (
                        <p className="text-sm text-gray-500">Loading mappings…</p>
                      ) : selectedUserMappings.length === 0 ? (
                        <p className="text-sm text-gray-500">No channel mappings for this user.</p>
                      ) : (
                        <div className="overflow-x-auto border border-gray-200 rounded-lg">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-left text-xs text-gray-600 uppercase">
                              <tr>
                                <th className="px-3 py-2">Guild / channel</th>
                                <th className="px-3 py-2">Platform</th>
                                <th className="px-3 py-2">Sync</th>
                                <th className="px-3 py-2">Active</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {selectedUserMappings.map((m) => (
                                <tr key={m.id || `${m.channelId}-${m.tradingPlatform}`}>
                                  <td className="px-3 py-2">
                                    <div className="font-medium text-gray-900">
                                      {m.channelName || m.channelId || '—'}
                                    </div>
                                    <div className="text-xs text-gray-500">{m.guildName || ''}</div>
                                  </td>
                                  <td className="px-3 py-2">
                                    {m.platformName || m.tradingPlatform || '—'}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                                    {m.syncTime
                                      ? new Date(m.syncTime).toLocaleString()
                                      : '—'}
                                  </td>
                                  <td className="px-3 py-2">{m.isActive ? 'Yes' : 'No'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Sync time uses the mapping row last update (UTC stored, shown in local time).
                      </p>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold text-gray-700 mb-2">Change history</h4>
                      {loadingUserAuditLog ? (
                        <p className="text-sm text-gray-500">Loading change history…</p>
                      ) : selectedUserAuditLog.length === 0 ? (
                        <p className="text-sm text-gray-500">No recorded config changes for this user.</p>
                      ) : (
                        <div className="overflow-x-auto border border-gray-200 rounded-lg">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-left text-xs text-gray-600 uppercase">
                              <tr>
                                <th className="px-3 py-2">When</th>
                                <th className="px-3 py-2">Field</th>
                                <th className="px-3 py-2">Old → New</th>
                                <th className="px-3 py-2">Action</th>
                                <th className="px-3 py-2">Source</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {selectedUserAuditLog.map((a) => (
                                <tr key={a.id || `${a.optionName}-${a.createdAt}`}>
                                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                                    {a.createdAt
                                      ? new Date(a.createdAt).toLocaleString()
                                      : '—'}
                                  </td>
                                  <td className="px-3 py-2 font-medium text-gray-900">
                                    {a.optionName || '—'}
                                  </td>
                                  <td className="px-3 py-2 text-gray-700">
                                    <span className="text-gray-500">{a.oldValue ?? '—'}</span>
                                    {' → '}
                                    <span className="text-gray-900">{a.newValue ?? '—'}</span>
                                  </td>
                                  <td className="px-3 py-2 text-gray-700">{a.action || '—'}</td>
                                  <td className="px-3 py-2 text-xs text-gray-500 break-all">
                                    {a.requestPath || '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Most recent 100 config/risk changes (UTC stored, shown in local time). Changed by the account holder.
                      </p>
                    </div>

                    <div>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                        <div>
                          <h4 className="text-lg font-semibold text-gray-700">Trade history</h4>
                          <p className="text-xs text-gray-500">
                            {ADMIN_TRADE_HISTORY_PAGE_SIZE} per page · Total matching: {selectedUserTradesTotal}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => selectedUser && loadUserTrades(selectedUser.id, adminTradeHistoryPage)}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm shrink-0"
                        >
                          Refresh
                        </button>
                      </div>
                      {selectedUserTrades.length > 0 ? (
                        <>
                          <div className="overflow-x-auto max-h-[28rem] overflow-y-auto border border-gray-200 rounded-lg">
                            <table className="min-w-full text-sm bg-white">
                              <thead className="bg-gray-50 text-left text-xs text-gray-600 uppercase sticky top-0">
                                <tr>
                                  <th className="px-3 py-2">Execution</th>
                                  <th className="px-3 py-2">Filled</th>
                                  <th className="px-3 py-2">Symbol</th>
                                  <th className="px-3 py-2">Action</th>
                                  <th className="px-3 py-2">Qty</th>
                                  <th className="px-3 py-2">Price</th>
                                  <th className="px-3 py-2">Total</th>
                                  <th className="px-3 py-2">Status</th>
                                  <th className="px-3 py-2">Channel</th>
                                  <th className="px-3 py-2">Platform</th>
                                  <th className="px-3 py-2">Env</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {selectedUserTrades.map((trade, index) => (
                                  <tr key={trade.id || index} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                                      {trade.createdAt ? formatDate(trade.createdAt) : '—'}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                                      {trade.filledAt ? formatDate(trade.filledAt) : '—'}
                                    </td>
                                    <td className="px-3 py-2 font-medium text-gray-900">{trade.symbol}</td>
                                    <td className="px-3 py-2">
                                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getActionColor(trade.action)}`}>
                                        {String(trade.action || '').toUpperCase()}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2">{trade.quantity}</td>
                                    <td className="px-3 py-2">
                                      {trade.price != null && trade.price !== '' ? formatCurrency(trade.price) : '—'}
                                    </td>
                                    <td className="px-3 py-2">
                                      {trade.totalAmount != null && trade.totalAmount !== ''
                                        ? formatCurrency(trade.totalAmount)
                                        : '—'}
                                    </td>
                                    <td className="px-3 py-2">
                                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(trade.status)}`}>
                                        {trade.status}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 text-gray-900">
                                      {trade.channelName || (trade.channelId ? `Channel ${trade.channelId}` : '—')}
                                    </td>
                                    <td className="px-3 py-2">
                                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                        trade.tradingPlatform === 'tradier' ? 'bg-blue-100 text-blue-800' :
                                        trade.tradingPlatform === 'ibkr' ? 'bg-purple-100 text-purple-800' :
                                        trade.tradingPlatform === 'schwab' ? 'bg-indigo-100 text-indigo-800' :
                                        'bg-gray-100 text-gray-800'
                                      }`}>
                                        {(trade.tradingPlatform || 'tradier').toUpperCase()}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2">
                                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                        (trade.apiEnvironment === 'sandbox' || trade.tradierUseSandbox)
                                          ? 'bg-yellow-100 text-yellow-800'
                                          : 'bg-green-100 text-green-800'
                                      }`}>
                                        {(trade.apiEnvironment === 'sandbox' || trade.tradierUseSandbox) ? 'Sandbox' : 'Production'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-3">
                            <p className="text-xs text-gray-500">
                              Page {adminTradeHistoryPage} of {adminTradeHistoryTotalPages}
                            </p>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                disabled={adminTradeHistoryPage <= 1 || loading}
                                onClick={() => selectedUser && loadUserTrades(selectedUser.id, adminTradeHistoryPage - 1)}
                                className="px-3 py-1 text-sm border rounded-lg disabled:opacity-40"
                              >
                                Prev
                              </button>
                              <button
                                type="button"
                                disabled={adminTradeHistoryPage >= adminTradeHistoryTotalPages || loading}
                                onClick={() => selectedUser && loadUserTrades(selectedUser.id, adminTradeHistoryPage + 1)}
                                className="px-3 py-1 text-sm border rounded-lg disabled:opacity-40"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <p className="text-gray-500 text-center py-8">No trades found for this user</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Select a user from the list to view details, mappings, and trade history</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {adminSubView === 'health' && (
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <ComponentStatus />
          </div>
        )}

        {adminSubView === 'logs' && (
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-1">Live tail logs</h2>
            <p className="text-sm text-gray-600 mb-4">
              Choose log groups, optional message filter, and ERROR/DEBUG toggles. Fetch on demand or auto-refresh
              every 15s.
            </p>
            <p className="text-xs text-gray-500 mb-3">
              Source: CloudWatch Logs via FilterLogEvents (live tail endpoint).
            </p>

            <div className="flex flex-wrap gap-4 items-end mb-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Time window</label>
                <select
                  value={adminLogsMinutesBack}
                  onChange={(e) => setAdminLogsMinutesBack(Number(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value={5}>Last 5 minutes</option>
                  <option value={15}>Last 15 minutes</option>
                  <option value={30}>Last 30 minutes</option>
                  <option value={60}>Last 60 minutes</option>
                  <option value={180}>Last 3 hours</option>
                  <option value={360}>Last 6 hours</option>
                  <option value={720}>Last 12 hours</option>
                  <option value={1440}>Last 24 hours</option>
                </select>
              </div>

              <div className="flex-1 min-w-[240px] relative" ref={adminLogGroupMenuRef}>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Log groups</label>
                <button
                  type="button"
                  onClick={() => setAdminLogGroupDropdownOpen((o) => !o)}
                  className="w-full flex items-center justify-between gap-2 border border-gray-300 rounded-lg px-3 py-2 text-sm text-left bg-white hover:bg-gray-50"
                >
                  <span className="truncate text-gray-800">{adminLogGroupButtonLabel()}</span>
                  <span className="text-gray-500 shrink-0">{adminLogGroupDropdownOpen ? '▲' : '▼'}</span>
                </button>
                {adminLogGroupDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full min-w-[320px] max-w-lg rounded-lg border border-gray-200 bg-white shadow-lg">
                    <div className="p-2 border-b border-gray-100">
                      <input
                        type="search"
                        value={adminLogGroupSearch}
                        onChange={(e) => setAdminLogGroupSearch(e.target.value)}
                        placeholder="Search by keyword"
                        className="w-full border border-blue-400 rounded-md px-2 py-1.5 text-sm"
                      />
                    </div>
                    <div className="max-h-64 overflow-y-auto p-2 space-y-1">
                      <label className="flex items-start gap-2 text-sm cursor-pointer rounded hover:bg-gray-50 p-1">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={allLogGroupsSelected}
                          onChange={toggleAllAdminLogGroups}
                          disabled={adminLogGroupOptions.length === 0}
                        />
                        <span>
                          <span className="font-semibold text-gray-900">All</span>
                          <span className="block text-xs text-gray-500">
                            Select all {adminLogGroupOptions.length} log groups
                          </span>
                        </span>
                      </label>
                      {filteredAdminLogGroupOptions.map((g) => (
                        <label
                          key={g}
                          className="flex items-start gap-2 text-sm cursor-pointer rounded hover:bg-gray-50 p-1"
                        >
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={adminLogGroupsSelected.includes(g)}
                            onChange={() => toggleAdminLogGroup(g)}
                          />
                          <span>
                            <span className="font-medium text-gray-900 break-all">{g}</span>
                            <span className="block text-xs text-gray-500">Standard</span>
                          </span>
                        </label>
                      ))}
                      {filteredAdminLogGroupOptions.length === 0 && (
                        <p className="text-xs text-gray-500 px-1 py-2">No groups match this search.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-[180px]">
                {/* Future enhancement: wire targeted order tracing here via /admin/search-traces
                    using traceId / userId / userEmail (with async 202 + jobId polling for heavy windows). */}
                <label className="block text-xs font-semibold text-gray-600 mb-1">Message contains (optional)</label>
                <input
                  type="text"
                  value={adminLogsKeyword}
                  onChange={(e) => setAdminLogsKeyword(e.target.value)}
                  placeholder="Filter log line text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={adminLogsIncludeError}
                  onChange={(e) => setAdminLogsIncludeError(e.target.checked)}
                />
                ERROR
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={adminLogsIncludeDebug}
                  onChange={(e) => setAdminLogsIncludeDebug(e.target.checked)}
                />
                DEBUG
              </label>
              <button
                type="button"
                onClick={fetchAdminRecentLogs}
                disabled={adminLogsLoading}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {adminLogsLoading ? 'Loading…' : 'Fetch logs'}
              </button>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={adminLogsAutoRefresh}
                  onChange={(e) => setAdminLogsAutoRefresh(e.target.checked)}
                />
                Auto-refresh (15s)
              </label>
            </div>

            <p className="text-xs text-gray-500 mb-2">
              {allLogGroupsSelected && adminLogGroupOptions.length > 0
                ? `All ${adminLogGroupOptions.length} log groups will be queried (same as YAML list).`
                : `${adminLogGroupsSelected.length} group${adminLogGroupsSelected.length === 1 ? '' : 's'} selected`}
              {adminLogGroupOptions.length > 0 && !allLogGroupsSelected
                ? ` · ${adminLogGroupOptions.length} available from API`
                : ''}
            </p>
            {adminLogGroupOptions.length > 0 && adminLogGroupsSelected.length > 0 && (
              <div className="flex flex-wrap gap-1 items-center mb-3 text-xs">
                <span className="font-semibold text-gray-600 shrink-0">Selected:</span>
                {allLogGroupsSelected ? (
                  <span className="bg-blue-100 text-blue-900 px-2 py-0.5 rounded border border-blue-200">
                    All {adminLogGroupOptions.length} groups
                  </span>
                ) : (
                  <>
                    {adminLogGroupsSelected.slice(0, 10).map((g) => (
                      <span
                        key={g}
                        className="bg-gray-100 text-gray-800 px-2 py-0.5 rounded max-w-[220px] truncate"
                        title={g}
                      >
                        {g.split('/').pop() || g}
                      </span>
                    ))}
                    {adminLogGroupsSelected.length > 10 && (
                      <span className="text-gray-500">+{adminLogGroupsSelected.length - 10} more</span>
                    )}
                  </>
                )}
              </div>
            )}

            {adminLogsError && <p className="text-sm text-red-600 mb-2">{adminLogsError}</p>}
            {adminLogsFilterPattern != null && adminLogsFilterPattern !== '' && (
              <p className="text-xs text-gray-500 mb-2">
                CloudWatch filter pattern:{' '}
                <code className="bg-gray-100 px-1 rounded">{adminLogsFilterPattern}</code>
              </p>
            )}

            <div className="max-h-[28rem] overflow-y-auto rounded-lg border border-gray-200 bg-black text-green-400 font-mono text-xs p-3">
              {adminLogsEvents.length === 0 && !adminLogsLoading ? (
                <span className="text-gray-500">No events loaded yet.</span>
              ) : (
                adminLogsEvents.map((ev, i) => (
                  <div
                    key={`${ev.timestamp}-${i}`}
                    className="border-b border-gray-800 py-1 whitespace-pre-wrap break-all"
                  >
                    <span className="text-gray-500">{ev.timestampIso}</span>
                    <span className="text-fuchsia-400 ml-2">{String(ev.logGroup).split('/').pop()}</span>
                    <span className="ml-2 text-green-300">{ev.message}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderVerificationForm = () => (
    <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md mx-auto my-8">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Verify Your Email</h2>
      <form onSubmit={handleVerifyCode} className="space-y-4">
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2">Verification Code</label>
          <input
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            type="text"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="Enter verification code"
            required
          />
          <p className="text-gray-500 text-sm mt-2">
            A verification code has been sent to your email {formState.email}. If you closed this page earlier, you can log in again with the same email and we&apos;ll resend a fresh code automatically.
          </p>
        </div>
        {error && <p className="text-red-500 text-center text-sm">{error}</p>}
        {message && <p className="text-green-500 text-center text-sm">{message}</p>}
        <button
          className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-xl hover:bg-blue-700 transition duration-200 shadow-lg disabled:opacity-50 flex items-center justify-center"
          type="submit"
          disabled={loading}
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Verifying...
            </>
          ) : 'Verify Email'}
        </button>
        <p className="text-center text-sm text-gray-500 mt-3">
          Didn&apos;t receive the email?{' '}
          {resendCooldownRemaining > 0 ? (
            <span className="text-gray-400">You can request a new code in {resendCooldownRemaining}s</span>
          ) : (
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendLoading}
              className="text-blue-600 font-medium hover:underline disabled:opacity-50"
            >
              {resendLoading ? 'Sending...' : 'Resend verification code'}
            </button>
          )}
        </p>
      </form>
    </div>
  );

  const renderForgotPasswordRequestForm = () => (
    <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md mx-auto my-8">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Reset password</h2>
      <p className="text-gray-600 text-sm text-center mb-4">
        Enter your account email. We&apos;ll send a verification code you can use on the next step to set a new password.
      </p>
      <form onSubmit={handleForgotPasswordSendCode} className="space-y-4">
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2">Email address</label>
          <input
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            type="email"
            name="email"
            value={formState.email}
            onChange={handleInputChange}
            placeholder="john.doe@example.com"
            required
            autoComplete="email"
          />
        </div>
        {error && <p className="text-red-500 text-center text-sm">{error}</p>}
        {message && <p className="text-green-600 text-center text-sm">{message}</p>}
        <button
          className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-xl hover:bg-blue-700 transition duration-200 shadow-lg disabled:opacity-50 flex items-center justify-center"
          type="submit"
          disabled={loading}
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending...
            </>
          ) : (
            'Send verification code'
          )}
        </button>
      </form>
      <div className="mt-4 text-center text-sm">
        <button
          type="button"
          onClick={() => {
            leaveForgotPasswordFlow();
            setIsLoginView(true);
          }}
          className="text-blue-600 font-bold hover:underline"
        >
          Back to login
        </button>
      </div>
    </div>
  );

  const renderForgotPasswordConfirmForm = () => (
    <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md mx-auto my-8">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Set new password</h2>
      <p className="text-gray-600 text-sm text-center mb-4">
        Enter the code from your email and your new password for{' '}
        <span className="font-medium text-gray-800">{formState.email || 'your account'}</span>.
      </p>
      <form onSubmit={handleForgotPasswordConfirmNewPassword} className="space-y-4">
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2">Verification code</label>
          <input
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            type="text"
            value={passwordResetCode}
            onChange={(e) => setPasswordResetCode(e.target.value)}
            placeholder="Code from email"
            required
            autoComplete="one-time-code"
          />
        </div>
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2">New password</label>
          <input
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            type="password"
            name="password"
            value={formState.password}
            onChange={handleInputChange}
            placeholder="New password"
            required
            autoComplete="new-password"
          />
        </div>
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2">Confirm new password</label>
          <input
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            type="password"
            name="confirmPassword"
            value={formState.confirmPassword}
            onChange={handleInputChange}
            placeholder="Confirm new password"
            required
            autoComplete="new-password"
          />
        </div>
        {error && <p className="text-red-500 text-center text-sm">{error}</p>}
        {message && <p className="text-green-600 text-center text-sm">{message}</p>}
        <button
          className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-xl hover:bg-blue-700 transition duration-200 shadow-lg disabled:opacity-50 flex items-center justify-center"
          type="submit"
          disabled={loading}
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Updating...
            </>
          ) : (
            'Update password'
          )}
        </button>
        <p className="text-center text-sm text-gray-500">
          Didn&apos;t receive the email?{' '}
          {passwordResetResendCooldownRemaining > 0 ? (
            <span className="text-gray-400">You can request a new code in {passwordResetResendCooldownRemaining}s</span>
          ) : (
            <button
              type="button"
              onClick={handleResendPasswordResetCode}
              disabled={passwordResetResendLoading}
              className="text-blue-600 font-medium hover:underline disabled:opacity-50"
            >
              {passwordResetResendLoading ? 'Sending...' : 'Resend code'}
            </button>
          )}
        </p>
      </form>
      <div className="mt-4 text-center text-sm space-y-2">
        <div>
          <button
            type="button"
            onClick={() => {
              setForgotPasswordView('send_code');
              setPasswordResetCode('');
              setError('');
              setMessage('');
            }}
            className="text-blue-600 font-bold hover:underline"
          >
            Use a different email
          </button>
        </div>
        <div>
          <button
            type="button"
            onClick={() => {
              leaveForgotPasswordFlow();
              setIsLoginView(true);
            }}
            className="text-gray-600 hover:underline"
          >
            Back to login
          </button>
        </div>
      </div>
    </div>
  );

  const renderRegisterForm = () => (
    <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md mx-auto my-8">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Create Account</h2>
      <form onSubmit={handleRegister} className="space-y-4">
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2">Email Address</label>
          <input
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            type="email"
            name="email"
            value={formState.email}
            onChange={handleInputChange}
            placeholder="john.doe@example.com"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Mobile phone number <span className="text-red-600">*</span>
          </label>
          <input
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            type="tel"
            name="phoneNumber"
            value={formState.phoneNumber}
            onChange={handleInputChange}
            pattern="[0-9]*"
            minLength="10"
            maxLength="15"
            placeholder="9876543210 (US) or 529876543210 (intl)"
            required
            autoComplete="tel"
          />
          <p className="text-gray-500 text-xs mt-1">
            Required for account security. We do not send text messages until you complete SMS consent separately.
            Enter numbers only:
          </p>
          <ul className="text-gray-500 text-xs mt-1 space-y-1 list-disc pl-4">
            <li>US numbers: "9876543210" (system adds +1)</li>
            <li>US with code: "19876543210" (becomes +19876543210)</li>
            <li>International: "529876543210" (becomes +529876543210 for Mexico)</li>
          </ul>
        </div>
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2">Password</label>
          <input
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            type="password"
            name="password"
            value={formState.password}
            onChange={handleInputChange}
            placeholder="Create a strong password"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2">Confirm Password</label>
          <input
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            type="password"
            name="confirmPassword"
            value={formState.confirmPassword}
            onChange={handleInputChange}
            placeholder="Confirm your password"
            required
          />
        </div>
        {error && <p className="text-red-500 text-center text-sm">{error}</p>}
        {message && <p className="text-green-500 text-center text-sm">{message}</p>}
        <button
          className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-xl hover:bg-blue-700 transition duration-200 shadow-lg disabled:opacity-50 flex items-center justify-center"
          type="submit"
          disabled={loading}
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Creating Account...
            </>
          ) : 'Register'}
        </button>
      </form>
      <div className="mt-4 text-center">
        {message ? (
          <div className="bg-green-50 p-4 rounded-lg mb-4">
            <p className="text-green-800 text-sm font-medium">{message}</p>
            <button
              onClick={() => setIsLoginView(true)}
              className="mt-3 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition duration-200"
            >
              Go to Login
            </button>
          </div>
        ) : (
          <div className="text-sm">
            <span>Already have an account? </span>
            <button onClick={() => setIsLoginView(true)} className="text-blue-600 font-bold hover:underline">
              Log in here
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderLoginForm = () => (
    <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md mx-auto my-8">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Login to Your Account</h2>
      <form onSubmit={handleLogin} className="space-y-4">
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2">Email Address</label>
          <input
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            type="email"
            name="email"
            value={formState.email}
            onChange={handleInputChange}
            placeholder="john.doe@example.com"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2">Password</label>
          <input
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            type="password"
            name="password"
            value={formState.password}
            onChange={handleInputChange}
            placeholder="Your password"
            required
          />
        </div>
        <div className="text-right">
          <button
            type="button"
            onClick={() => {
              setError('');
              setMessage('');
              setForgotPasswordView('send_code');
            }}
            className="text-sm text-blue-600 font-medium hover:underline"
          >
            Forgot password?
          </button>
        </div>
        {error && <p className="text-red-500 text-center text-sm">{error}</p>}
        {message && <p className="text-green-600 text-center text-sm">{message}</p>}
        <button
          className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-xl hover:bg-blue-700 transition duration-200 shadow-lg disabled:opacity-50 flex items-center justify-center"
          type="submit"
          disabled={loading}
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Logging In...
            </>
          ) : 'Log In'}
        </button>
      </form>
      <div className="mt-4 text-center text-sm">
        <span>Don't have an account? </span>
        <button
          onClick={() => {
            setForgotPasswordView(null);
            setIsLoginView(false);
          }}
          className="text-blue-600 font-bold hover:underline"
        >
          Register here
        </button>
      </div>
    </div>
  );

  if (!appInitialized) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const hasTokens = userData?.hasTokens;

  return (
    <div className="min-h-screen bg-gray-50">
      {isLoggedIn && renderNavigation()}

      <div className="p-4 md:p-8 flex items-center justify-center">
        {!isLoggedIn ? (
          <>
            {!isSessionSnapshotCleared(sessionSnapshotForTests) && (
              <span data-testid="stale-user-data-leak" className="sr-only" aria-hidden="true" />
            )}
            {isVerifying ? (
            renderVerificationForm()
          ) : forgotPasswordView === 'send_code' ? ( // pragma: allowlist secret
            renderForgotPasswordRequestForm()
          ) : forgotPasswordView === 'set_password' ? ( // pragma: allowlist secret
            renderForgotPasswordConfirmForm()
          ) : isLoginView ? (
            renderLoginForm()
          ) : (
            renderRegisterForm()
          )}
          </>
        ) : (
          <>
            {currentView === 'dashboard' && (
              isMobileDashboard ? (
                <MobileDashboard
                  userData={userData}
                  userSettings={userSettings}
                  tradeHistory={tradeHistory}
                  tradingAnalytics={tradingAnalytics}
                  channelMappings={channelMappings}
                  analyticsPeriod={analyticsPeriod}
                  onAnalyticsPeriodChange={setAnalyticsPeriod}
                  isAdmin={isAdmin}
                  onNavigate={setCurrentView}
                  loading={loadingDashboard || loadingAnalytics}
                  canAccessChannelConfig={canAccessChannelConfig}
                  canAccessTradeHistory={canAccessTradeHistory}
                  hasAcceptedTerms={hasAcceptedTerms}
                  hasAcceptedSms={hasAcceptedSms}
                  hasTradingConfig={hasTradingConfig}
                  termsReminderMessage={termsReminderMessage}
                  smsReminderMessage={smsReminderMessage}
                  onOpenAutoApproveReconfirmModal={(mm) => setAutoApproveReconfirmModalMapping(mm)}
                  autoApproveReconfirmFromApi={autoApproveReconfirmFromApi}
                />
              ) : (
                renderDashboard()
              )
            )}
            {currentView === 'documents' && !isAdmin && renderDocuments()}
            {autoApproveReconfirmModalMapping && (
              <AutoApproveReconfirmModal
                open
                mapping={autoApproveReconfirmModalMapping}
                ui={computeAutoApproveReconfirmUi(
                  autoApproveReconfirmModalMapping,
                  new Date(),
                  autoApproveReconfirmFromApi
                )}
                onClose={() => {
                  if (!autoApproveReconfirmBusy) setAutoApproveReconfirmModalMapping(null);
                }}
                onConfirm={submitAutoApproveReconfirm}
                confirming={autoApproveReconfirmBusy}
              />
            )}
            {currentView === 'agreements_terms' && (
              <TermsAgreementView
                isSigned={hasAcceptedTerms}
                onStartSigning={() => startConsentSigning('terms')}
                onCancel={() => setCurrentView('dashboard')}
                onSigningSessionEnd={() => setConsentSigningUrls((prev) => ({ ...prev, terms: '' }))}
                onRefreshStatus={async () => {
                  const data = await refreshConsentStatusAfterSigning('terms');
                  if (data?.hasAcceptedTerms) {
                    setCurrentView('dashboard');
                  } else {
                    setConsentActionError(
                      'We could not confirm the signed status yet. The update may take a short time — return to the dashboard and refresh, or try again in a moment.'
                    );
                  }
                }}
                signingUrl={consentSigningUrls.terms}
                isStarting={startingConsentType === 'terms'}
                errorMessage={consentActionError}
              />
            )}
            {currentView === 'agreements_sms' && (
              <SmsAgreementView
                isSigned={hasAcceptedSms}
                onStartSigning={() => startConsentSigning('sms')}
                onCancel={() => setCurrentView('dashboard')}
                onSigningSessionEnd={() => setConsentSigningUrls((prev) => ({ ...prev, sms: '' }))}
                onRefreshStatus={async () => {
                  const data = await refreshConsentStatusAfterSigning('sms');
                  if (data?.hasAcceptedSms) {
                    setCurrentView('dashboard');
                  } else {
                    setConsentActionError(
                      'We could not confirm the signed status yet. The update may take a short time — return to the dashboard and refresh, or try again in a moment.'
                    );
                  }
                }}
                signingUrl={consentSigningUrls.sms}
                isStarting={startingConsentType === 'sms'}
                errorMessage={consentActionError}
              />
            )}
            {currentView === 'trading_history' &&
              canAccessTradeHistory &&
              !isAdmin &&
              renderTradeHistory()}
            {currentView === 'channel_config' &&
              canAccessChannelConfig &&
              !isAdmin &&
              renderChannelConfig()}
            {/* Trading Analytics view temporarily disabled */}
            {/* {currentView === 'trading_analytics' && renderTradingAnalytics()} */}
            {currentView === 'help_support' && user && <HelpSupportView />}
            {currentView === 'settings' && renderSettingsForm()}
            {currentView === 'admin' && renderAdminDashboard()}
          </>
        )}
      </div>
    </div>
  );
};

const App = () => (
  <AgreementStatusProvider>
    <AppInner />
  </AgreementStatusProvider>
);

export default App;
